import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { format_date, required, alert, GET, POST, PUT, DELETE, loading, Select, profile, toFixed, DateInput } from "../components/CustomComponent";
import useIsMobile from "../components/useIsMobile";

const API_PROJECTS = "v1/admin/project-timelines";
const API_TASKS = "v1/admin/project-tasks";

// ราคาเต็ม/งวดชำระเงิน เห็นและแก้ได้เฉพาะ user_type = super_user (backend ตัด field ออกให้ user อื่นอีกชั้น)
const IS_SUPER_USER = profile?.user_type === "super_user";

// แปลงข้อมูลจาก API -> โครงสร้างที่หน้าเดิมใช้
const mapApiProject = (p) => ({
  id: p.uid,
  key: p.project_code || "",
  name: p.name,
  owner: p.owner || "",
  members: Array.isArray(p.members) ? p.members : [],
  status: p.status || "Planned",
  platform: p.platform || "",
  start_date: p.start_date,
  due_date: p.due_date,
  range_start: p.range_start || "",
  range_end: p.range_end || "",
  total_weeks: p.total_weeks || 0,
  // field การเงินมีเฉพาะตอน super_user เรียก (user อื่น backend ไม่ส่งมา)
  total_price: p.total_price ?? null,
  installments: Array.isArray(p.payment_installments) ? p.payment_installments : [],
  payment_summary: p.payment_summary || null,
  tasks: (p.tasks || []).map((t) => ({
    id: t.uid,
    order: t.order || "",
    name: t.name,
    type: t.type || "Task",
    start_date: t.start_date,
    due_date: t.due_date,
    status: t.status || "Planned",
    assignee: t.assignee || "",
    parentId: t.parent_uid || null,
    level: t.level || 0,
    progress: t.progress || 0,
    priority: t.priority || "Medium",
    description: t.description || "",
  })),
});

// ---- Brand palette (อ้างอิงจาก Navbar/AppShell ของโปรเจกต์) ----
const BRAND_BLUE = "#0f6bdc";

// ---- สีแถบ Gantt ตามสถานะ (ใช้โทนแบรนด์ ฟ้า–ไซแอน) ----
const statusBarColor = {
  Done: { from: "#34d399", to: "#10b981" }, // เขียว = เสร็จแล้ว
  "In Progress": { from: "#1e89e4", to: "#0f6bdc" }, // ฟ้าแบรนด์ = กำลังทำ
  Planned: { from: "#5fe3e8", to: "#27d6dd" }, // ไซแอนแบรนด์ = วางแผนไว้
};
const TODAY_ACCENT = { from: "#f05252", to: "#dc2626" }; // แดง = จุดเน้น "วันนี้"

// แถบไล่เฉดให้ดูมีมิติ
const barGradient = (c) => `linear-gradient(180deg, ${c.from} 0%, ${c.to} 100%)`;

const statusBadge = {
  Planned: { background: "rgba(39, 214, 221, 0.18)", color: "#0e7c84" },
  "In Progress": { background: "rgba(15, 107, 220, 0.16)", color: "#0b4fa0" },
  Done: { background: "rgba(16, 185, 129, 0.16)", color: "#047857" },
};

// ---- Header theme ----
const HEADER_TITLE_BG = "#1f3ec9"; // แถบหัวเรื่อง (น้ำเงินสด)
const HEADER_NAVY = "#1f3ec9"; // หัวคอลัมน์ซ้าย — สีเดียวกับหัวเรื่อง
// สีรายเดือน (สดและแยกกันชัด) วนซ้ำ: น้ำเงิน-ม่วง-ฟ้า-เทียล-เขียว-แดง-อำพัน
const monthPalette = ["#3949c4", "#7b3fc0", "#2f7fd6", "#1f9bb5", "#34a64a", "#d8443a", "#e08a2b"];
// แถวสัปดาห์ = เฉดอ่อนของสีเดือนนั้น (เคลือบขาวบาง ๆ)
const lighten = (hex) => `linear-gradient(rgba(255,255,255,0.26), rgba(255,255,255,0.26)), ${hex}`;
const WEEK_TODAY_BG = "linear-gradient(180deg, #f05252 0%, #dc2626 100%)"; // สัปดาห์ปัจจุบัน (แดง)
const HEADER_WHITE_BORDER = "2px solid #ffffff"; // เส้นขอบขาวคั่นช่อง

const PF_ORDER_KEY = "ptn-portfolio-order"; // ลำดับแถวภาพรวมที่ผู้ใช้จัดเอง (เก็บฝั่ง client)

// ---- แท็กประเภทงานของโปรเจกต์ (Web / Mobile) ----
const platformOptions = ["Web", "Mobile", "Web & Mobile"];
const platformTag = {
  Web: { icon: "fa-solid fa-globe", background: "rgba(15, 107, 220, 0.14)", color: "#0b4fa0" },
  Mobile: { icon: "fa-solid fa-mobile-screen-button", background: "rgba(123, 63, 192, 0.14)", color: "#6d28d9" },
  "Web & Mobile": { icon: "fa-solid fa-layer-group", background: "rgba(16, 185, 129, 0.16)", color: "#047857" },
};

// ---- ผู้รับผิดชอบโปรเจกต์ (เลือกจากพนักงาน) แยกตามบทบาท ----
const MEMBER_ROLES = [
  { value: "owner", label: "ผู้รับผิดชอบ", icon: "fa-solid fa-user-tie", color: "#0b4fa0", background: "rgba(15,107,220,0.10)" },
  { value: "pm", label: "PM", icon: "fa-solid fa-clipboard-list", color: "#7c3aed", background: "rgba(124,58,237,0.10)" },
  { value: "dev", label: "Dev", icon: "fa-solid fa-code", color: "#0f766e", background: "rgba(15,118,110,0.10)" },
  { value: "tester", label: "Tester", icon: "fa-solid fa-vial", color: "#c2410c", background: "rgba(234,88,12,0.10)" },
];
const memberRoleOptions = MEMBER_ROLES.map((r) => ({ value: r.value, label: r.label }));
const memberDisplayName = (m) => m.nickname || m.name || "(ไม่พบพนักงาน)";

// แท็กผู้รับผิดชอบรวมตามบทบาท เช่น [ผู้รับผิดชอบ: ไอซ์, บี] [Tester: ซี] — คืน null ถ้ายังไม่มีใคร
const renderMemberTags = (members, { fontSize = 10, onDark = false } = {}) => {
  const groups = MEMBER_ROLES.map((role) => ({ role, people: (members || []).filter((m) => m.role === role.value) })).filter((g) => g.people.length);
  if (!groups.length) return null;
  return groups.map(({ role, people }) => (
    <span
      key={role.value}
      className="rounded-pill px-2 d-inline-flex align-items-center gap-1 text-truncate"
      title={`${role.label}: ${people.map((m) => m.name || memberDisplayName(m)).join(", ")}`}
      style={{ background: onDark ? "#ffffff" : role.background, color: role.color, fontSize, lineHeight: 1.6, maxWidth: "100%", fontWeight: 600 }}
    >
      <i className={role.icon} style={{ fontSize: fontSize - 1 }} />
      <span className="text-truncate">{people.map(memberDisplayName).join(", ")}</span>
    </span>
  ));
};

// แสดงแท็กประเภทงาน — คืน null ถ้ายังไม่ได้ระบุ
// onDark = วางบนแถบหัวสีน้ำเงินเข้ม ต้องใช้พื้นขาวทึบ ไม่งั้นตัวอักษรจมไปกับพื้นหลัง
const renderPlatformTag = (platform, { fontSize = 10, iconOnly = false, onDark = false } = {}) => {
  const tag = platformTag[platform];
  if (!tag) return null;
  return (
    <span
      className={`rounded-pill d-inline-flex align-items-center gap-1 flex-shrink-0 fw-semibold ${iconOnly ? "justify-content-center" : "px-2"}`}
      title={`ประเภทงาน: ${platform}`}
      style={{
        background: onDark ? "#ffffff" : tag.background,
        color: tag.color,
        fontSize,
        lineHeight: 1.6,
        width: iconOnly ? 16 : undefined,
        height: iconOnly ? 16 : undefined,
        boxShadow: onDark ? "0 1px 3px rgba(0,0,0,0.25)" : undefined,
      }}
    >
      <i className={tag.icon} style={{ fontSize: fontSize - 1 }} />
      {!iconOnly && platform}
    </span>
  );
};

// ---- ราคาและงวดชำระเงิน (super_user) ----
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const fmtMoney = (n) => toFixed(round2(n));
const sumPercent = (installments) => round2(installments.reduce((sum, i) => sum + (Number(i.percent) || 0), 0));
const installmentAmount = (price, percent) => round2(((Number(price) || 0) * (Number(percent) || 0)) / 100);
const newInstallment = (no, percent = "") => ({ label: `งวดที่ ${no}`, percent, is_paid: false, paid_date: "", note: "" });
// รูปแบบงวดที่ใช้บ่อย กดทีเดียวแล้วค่อยปรับ
const installmentPresets = [
  { label: "50 / 50", parts: [50, 50] },
  { label: "30 / 40 / 30", parts: [30, 40, 30] },
  { label: "30 / 30 / 40", parts: [30, 30, 40] },
  { label: "25 × 4", parts: [25, 25, 25, 25] },
];
// รับเฉพาะตัวเลขกับจุดทศนิยม (ไม่เอาคอมมาที่ผู้ใช้อาจพิมพ์มา)
const cleanNumberInput = (value) => String(value ?? "").replace(/[^\d.]/g, "");

const priorityOptions = ["Highest", "High", "Medium", "Low"];
const issueTypeOptions = ["Task"]; // "Module", "Epic", "Task", "Sub-task"
const statusOptions = ["Planned", "In Progress", "Done"];

// ---- สถานะของโปรเจกต์ (ใช้ตัดสินว่าอยู่กลุ่มไหน) ----
// "Planned" = ยังไม่เริ่ม — ผู้ใช้กำหนดเองจากดรอปดาวน์ ไม่ได้เดาจากวันที่ของ task
const PROJECT_STATUS_NOT_STARTED = "Planned";
const projectStatusOptions = [
  { value: "Planned", label: "ยังไม่เริ่ม" },
  { value: "In Progress", label: "กำลังดำเนินการ" },
  { value: "Done", label: "เสร็จแล้ว" },
];
const projectStatusLabel = (status) => projectStatusOptions.find((o) => o.value === status)?.label || status || "-";

const toDate = (value) => new Date(value + "T00:00:00");

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const dayDiff = (start, end) => Math.round((toDate(end) - toDate(start)) / (1000 * 3600 * 24));

const formatISO = (date) => new Date(date).toISOString().slice(0, 10);
const formatISOLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// ---- helper สำหรับช่วงเวลา (เดือน/ปี) ----
const monthStr = (date) => formatISO(date).slice(0, 7); // "YYYY-MM"
const startOfMonth = (ym) => new Date(`${ym}-01T00:00:00`); // วันแรกของเดือน
const endOfMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m, 0); // วันสุดท้ายของเดือน (m เป็น 1-based พอดี)
};

const getDateRange = (tasks) => {
  if (!tasks.length) return [new Date(), new Date()];
  const starts = tasks.map((task) => toDate(task.start_date));
  const dues = tasks.map((task) => toDate(task.due_date));
  return [new Date(Math.min(...starts)), new Date(Math.max(...dues))];
};

// แบ่งช่วงเวลาเป็นสัปดาห์ (W1, W2, ...) เริ่มจากวันเริ่มโปรเจกต์
const buildWeekLabels = (startDate, endDate) => {
  const labels = [];
  let current = new Date(startDate);
  let weekIndex = 1;
  while (current <= endDate) {
    const weekStart = new Date(current);
    const weekEnd = addDays(current, 6);
    labels.push({ index: weekIndex, label: `W${weekIndex}`, start: weekStart, end: weekEnd });
    current = addDays(weekEnd, 1);
    weekIndex += 1;
  }
  return labels;
};

const computeParentTaskMetrics = (tasks) => {
  const map = tasks.reduce((acc, task) => ({ ...acc, [task.id]: { ...task } }), {});
  const childrenByParent = tasks.reduce((acc, task) => {
    if (task.parentId) acc[task.parentId] = [...(acc[task.parentId] || []), task.id];
    return acc;
  }, {});

  const visit = (taskId) => {
    const task = map[taskId];
    const children = childrenByParent[taskId] || [];
    if (!children.length) return task;
    const childResults = children.map(visit);
    const computedStart = new Date(Math.min(...childResults.map((c) => toDate(c.start_date))));
    const computedDue = new Date(Math.max(...childResults.map((c) => toDate(c.due_date))));
    const doneCount = childResults.filter((c) => c.status === "Done").length;
    const progress = Math.round((doneCount / childResults.length) * 100);
    const status = childResults.every((c) => c.status === "Done") ? "Done" : task.status === "Done" ? "Done" : "In Progress";
    map[taskId] = { ...task, start_date: formatISO(computedStart), due_date: formatISO(computedDue), progress, status };
    return map[taskId];
  };

  tasks.filter((task) => !task.parentId).forEach((task) => visit(task.id));
  return tasks.map((task) => map[task.id]);
};

// ไม่ทำ group/collapse แล้ว — แสดงเป็นรายการเดี่ยว (ตัดแถว module ที่เป็นหัวกลุ่มออก)
// เหลือเฉพาะงานจริง (ใบ + งานเดี่ยว) เรียงตามลำดับ order
const getFlatTasks = (tasks) => {
  const hasChildren = (id) => tasks.some((t) => t.parentId === id);
  return tasks
    .filter((task) => !hasChildren(task.id))
    .sort((a, b) => a.order.localeCompare(b.order, undefined, { numeric: true }));
};

const getProjectProgress = (tasks) => {
  if (!tasks.length) return 0;
  const rootTasks = tasks.filter((task) => !task.parentId);
  const done = rootTasks.filter((task) => task.status === "Done").length;
  return rootTasks.length ? Math.round((done / rootTasks.length) * 100) : 0;
};

// ---- AI วิเคราะห์ลำดับงาน (heuristic) ----
const PRIORITY_WEIGHT = { Highest: 4, High: 3, Medium: 2, Low: 1 };

// ให้คะแนนความเร่งด่วนของ task แต่ละตัว (ยิ่งมากยิ่งควรทำก่อน)
const scoreTask = (task, todayStr) => {
  if (task.status === "Done") return null;
  const daysToDue = dayDiff(todayStr, task.due_date); // ติดลบ = เลยกำหนด
  const pw = PRIORITY_WEIGHT[task.priority] || 2;
  let score = pw * 20;
  let reason = "ตามแผน";
  let tone = "plan";

  if (daysToDue < 0) {
    score += 1000 + Math.min(-daysToDue, 365); // เลยกำหนด = ด่วนสุด
    reason = `เลยกำหนดมาแล้ว ${-daysToDue} วัน`;
    tone = "overdue";
  } else if (daysToDue <= 14) {
    score += 400 - daysToDue * 10; // ใกล้ครบกำหนด
    reason = daysToDue === 0 ? "ครบกำหนดวันนี้" : `ครบกำหนดในอีก ${daysToDue} วัน`;
    tone = "soon";
  }

  if (task.status === "In Progress") {
    score += 300; // งานที่ทำค้างอยู่ควรสานต่อ
    if (tone === "plan") {
      reason = "กำลังทำอยู่ ควรสานต่อให้จบ";
      tone = "doing";
    }
  }

  return { score, reason, tone, daysToDue };
};

// วิเคราะห์ทุกโปรเจกต์ -> จัดลำดับงานที่ควรทำก่อน + โปรเจกต์ที่ควรโฟกัส
const analyzeProjects = (projects, todayStr) => {
  const rankedTasks = [];
  const projectSummaries = [];

  projects.forEach((project) => {
    const leafTasks = getFlatTasks(project.tasks || []);
    const active = leafTasks.filter((t) => t.status !== "Done");
    const overdue = active.filter((t) => dayDiff(todayStr, t.due_date) < 0);
    const dueSoon = active.filter((t) => {
      const d = dayDiff(todayStr, t.due_date);
      return d >= 0 && d <= 14;
    });
    const doing = active.filter((t) => t.status === "In Progress");

    let urgencySum = 0;
    leafTasks.forEach((t) => {
      const s = scoreTask(t, todayStr);
      if (!s) return;
      urgencySum += s.score;
      rankedTasks.push({
        projectId: project.id,
        projectKey: project.key,
        projectName: project.name,
        task: t,
        ...s,
      });
    });

    projectSummaries.push({
      id: project.id,
      key: project.key,
      name: project.name,
      progress: getProjectProgress(project.tasks),
      total: leafTasks.length,
      overdue: overdue.length,
      dueSoon: dueSoon.length,
      doing: doing.length,
      urgencySum,
    });
  });

  rankedTasks.sort((a, b) => b.score - a.score);
  projectSummaries.sort((a, b) => b.urgencySum - a.urgencySum);

  const focus = projectSummaries.find((p) => p.urgencySum > 0) || projectSummaries[0] || null;

  return {
    focus,
    ranked: rankedTasks.slice(0, 10),
    projectSummaries,
    totalActive: rankedTasks.length,
  };
};

// ---- ภาพรวมระดับพอร์ตโฟลิโอ: ระยะเวลาของแต่ละโปรเจกต์ + การซ้อนทับกันของไทม์ไลน์ ----
const MS_DAY = 1000 * 3600 * 24;
const daysBetween = (a, b) => Math.round((b - a) / MS_DAY);

// ช่วงเวลาจริงของโปรเจกต์ — อิงจากงานทั้งหมดก่อน ถ้ายังไม่มีงานค่อยใช้ช่วงที่บันทึกไว้/วันที่ของโปรเจกต์
const getProjectSpan = (project) => {
  const dates = (project.tasks || []).flatMap((t) => [t.start_date, t.due_date]).filter(Boolean);
  if (dates.length) {
    const ds = dates.map(toDate);
    return { start: new Date(Math.min(...ds)), end: new Date(Math.max(...ds)) };
  }
  if (project.range_start && project.range_end) return { start: startOfMonth(project.range_start), end: endOfMonth(project.range_end) };
  if (project.start_date && project.due_date) return { start: toDate(project.start_date), end: toDate(project.due_date) };
  return null;
};

// สร้างช่องเดือน (ความกว้างเท่ากันทุกเดือน) สำหรับแกนเวลาของภาพรวม
const buildMonthCells = (start, end) => {
  const cells = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= last) {
    const cellStart = new Date(cur);
    const cellEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
    cells.push({
      ym: `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`,
      start: cellStart,
      end: cellEnd,
      label: format_date(formatISOLocal(cellStart), "MMM yyyy", "en"),
      month: format_date(formatISOLocal(cellStart), "MMM", "en"),
      year: cellStart.getFullYear(),
    });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return cells;
};

// ตำแหน่ง (หน่วย = จำนวนเดือน) ของวันที่หนึ่ง ๆ บนแกนเวลา — ใช้เศษของเดือนเพื่อให้แถบตรงวันจริง
const monthPosStart = (months, date) => {
  const i = months.findIndex((m) => date >= m.start && date <= m.end);
  if (i === -1) return date < months[0].start ? 0 : months.length;
  return i + (date.getDate() - 1) / months[i].end.getDate();
};
const monthPosEnd = (months, date) => {
  const i = months.findIndex((m) => date >= m.start && date <= m.end);
  if (i === -1) return date < months[0].start ? 0 : months.length;
  return i + date.getDate() / months[i].end.getDate();
};

// ระดับความหนาแน่นของงานในเดือนนั้น (มีกี่โปรเจกต์ที่รันพร้อมกัน)
const loadTone = (n) => {
  if (n === 0) return { bg: "#f1f5f9", color: "#94a3b8", label: "ว่าง" };
  if (n === 1) return { bg: "rgba(16,185,129,0.18)", color: "#047857", label: "สบาย" };
  if (n === 2) return { bg: "rgba(15,107,220,0.16)", color: "#0b4fa0", label: "ปกติ" };
  if (n === 3) return { bg: "rgba(245,158,11,0.20)", color: "#b45309", label: "แน่น" };
  return { bg: "rgba(220,38,38,0.20)", color: "#b91c1c", label: "หนักมาก" };
};

const todayIso = () => formatISO(new Date());

const createNewTask = (parentId) => ({
  id: `new-${Date.now()}`,
  order: "",
  name: "",
  type: "Task",
  start_date: todayIso(),
  due_date: todayIso(),
  status: "Planned",
  assignee: "",
  parentId: parentId || null,
  level: parentId ? 1 : 0,
  progress: 0,
  description: "",
  priority: "Medium",
});

// ---- ขนาดคอลัมน์ฝั่งซ้าย (แคบลง เพื่อเน้นพื้นที่ timeline ฝั่งขวา) ----
// มือถือเหลือ 3 คอลัมน์ ไม่งั้นคอลัมน์ปักหมุดกิน 524px บนจอ 375px จนไม่เหลือที่ให้แถบ Gantt
const COLS_DESKTOP = [
  { key: "no", label: "No.", width: 62 },
  { key: "task", label: "Task", width: 184 },
  { key: "start", label: "Start", width: 76 },
  { key: "due", label: "Due", width: 76 },
  { key: "wks", label: "Wk", width: 36 },
  { key: "status", label: "Status", width: 90 },
];
// มือถือเหลือ 2 คอลัมน์ สถานะย่อเป็นจุดสีหน้าชื่องาน วันที่ไปอยู่บรรทัดล่าง
// แก้ไขผ่านโมดัลแทน ไม่วาง input/select ในตาราง เพราะ style.css บังคับ
// ช่องกรอกบนมือถือเป็น 16px (กัน iOS ซูม) ซึ่งทำให้แถวสูงจนตารางแน่นเกินอ่าน
const COLS_MOBILE = [
  { key: "no", label: "#", width: 26 },
  { key: "task", label: "Task", width: 152 },
];

const buildColumnOffsets = (cols) =>
  cols.reduce((acc, col, i) => {
    acc[col.key] = i === 0 ? 0 : acc[cols[i - 1].key] + cols[i - 1].width;
    return acc;
  }, {});

export default function ProjectTimeline() {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [modalTask, setModalTask] = useState(null);
  const [projectForm, setProjectForm] = useState(null); // null = ปิด modal สร้างโปรเจกต์
  const [dragIndex, setDragIndex] = useState(null); // แถวที่กำลังลาก
  const [overIndex, setOverIndex] = useState(null); // แถวที่ลากผ่าน
  const [analysis, setAnalysis] = useState(null); // ผลวิเคราะห์ AI (null = ปิด)
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showDragHelp, setShowDragHelp] = useState(false);
  const [showTaskHelp, setShowTaskHelp] = useState(false);
  const [showActiveList, setShowActiveList] = useState(true); // แสดง/ซ่อนรายการโปรเจกต์ที่กำลังดำเนินการ
  const [showDoneList, setShowDoneList] = useState(false); // แสดง/ซ่อนรายการโปรเจกต์ที่เสร็จแล้ว
  const [showUpcomingList, setShowUpcomingList] = useState(false); // แสดง/ซ่อนรายการโปรเจกต์ที่ยังไม่เริ่ม (เริ่มต้นย่อไว้)
  const [includeDoneInOverview, setIncludeDoneInOverview] = useState(false); // นับโปรเจกต์ที่เสร็จแล้วในภาพรวมด้วยหรือไม่
  // ลำดับแถวในภาพรวมที่ผู้ใช้ลากจัดเอง (เก็บที่เครื่อง ไม่ยุ่งกับ API) — ว่าง = เรียงตามวันเริ่มอัตโนมัติ
  const [pfOrder, setPfOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PF_ORDER_KEY) || "[]");
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  });
  const [pfDragIndex, setPfDragIndex] = useState(null); // แถวที่กำลังลากในภาพรวม
  const [pfOverIndex, setPfOverIndex] = useState(null); // แถวที่ลากผ่านในภาพรวม
  const selectedProject = projects.find((p) => p.id === selectedProjectId) || projects[0];

  // มือถือ: คอลัมน์ปักหมุดเหลือ 3 ช่อง และช่องสัปดาห์แคบลง ให้เห็นแถบ Gantt ได้จริง
  const isMobile = useIsMobile();
  const cols = isMobile ? COLS_MOBILE : COLS_DESKTOP;
  const COL_LEFT = useMemo(() => buildColumnOffsets(cols), [cols]);
  const INFO_WIDTH = useMemo(() => cols.reduce((sum, col) => sum + col.width, 0), [cols]);
  // ความสูงแถวหัวบน ใช้เป็น offset ของ sticky แถวสัปดาห์ ต้องขยับตามที่บีบ padding บนมือถือ
  const HEADER_ROW_H = isMobile ? 18 : 28;
  const WEEK_W = isMobile ? 34 : 56; // สัปดาห์กว้างขึ้นบนจอใหญ่ ให้แถบ Gantt อ่านง่ายและเด่น
  // ---- ขนาดของภาพรวมพอร์ตโฟลิโอ (แถวละ 1 โปรเจกต์ แกนนอนเป็นเดือน) ----
  const PF_LABEL_W = isMobile ? 124 : 208;
  const PF_MONTH_W = isMobile ? 58 : 92;
  const PF_ROW_H = isMobile ? 36 : 40;

  // โหลดโปรเจกต์ทั้งหมด (รวม tasks) จาก API
  const getData = useCallback(async (focusCode) => {
    loading(true);
    const res = await GET(API_PROJECTS, { page: 0 });
    if (res?.status) {
      const mapped = (res.data || []).map(mapApiProject);
      setProjects(mapped);
      setSelectedProjectId((prev) => {
        if (focusCode) return mapped.find((p) => p.key === focusCode)?.id || prev;
        return mapped.some((p) => p.id === prev) ? prev : mapped[0]?.id || "";
      });
    }
    loading(false);
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  // รายชื่อพนักงานสำหรับเลือกผู้รับผิดชอบ (endpoint ของ timeline เอง ไม่ต้องมีสิทธิ์ดูหน้าพนักงาน)
  const [memberOptions, setMemberOptions] = useState([]);
  useEffect(() => {
    GET(`${API_PROJECTS}/member-options`).then((res) => {
      if (res?.status) setMemberOptions(res.data || []);
    });
  }, []);
  const tasks = useMemo(() => computeParentTaskMetrics(selectedProject?.tasks || []), [selectedProject]);
  const today = todayIso();

  const summaryValues = useMemo(() => {
    const activeProjects = projects.filter((p) => p.status === "In Progress").length;
    const doneProjects = projects.filter((p) => p.status === "Done").length;
    const overdueTasks = projects.reduce((total, project) => {
      const overdue = project.tasks.filter((t) => t.status !== "Done" && toDate(t.due_date) < toDate(today));
      return total + overdue.length;
    }, 0);
    const overallPerformance = projects.length
      ? Math.round(projects.reduce((sum, p) => sum + getProjectProgress(p.tasks), 0) / projects.length)
      : 0;
    return { activeProjects, doneProjects, overdueTasks, overallPerformance };
  }, [projects, today]);

  const visibleTasks = useMemo(() => getFlatTasks(tasks), [tasks]);

  // ---- แยกโปรเจกต์ออกเป็น 2 กลุ่ม: กำลังดำเนินการ / เสร็จแล้ว ----
  const activeProjectList = useMemo(() => projects.filter((p) => p.status !== "Done"), [projects]);

  // ---- ภาพรวมไทม์ไลน์ทุกโปรเจกต์: ระยะเวลาแต่ละโปรเจกต์ + ช่วงที่ซ้อนทับกัน + โหลดรายเดือน ----
  const portfolio = useMemo(() => {
    const source = includeDoneInOverview ? projects : activeProjectList;
    const rows = source
      .map((p) => {
        const span = getProjectSpan(p);
        if (!span) return null;
        const days = daysBetween(span.start, span.end) + 1;
        return {
          id: p.id,
          key: p.key,
          name: p.name,
          owner: p.owner,
          status: p.status,
          platform: p.platform || "",
          progress: getProjectProgress(p.tasks),
          taskCount: getFlatTasks(p.tasks || []).length,
          start: span.start,
          end: span.end,
          startIso: formatISOLocal(span.start),
          endIso: formatISOLocal(span.end),
          days,
          weeks: Math.max(1, Math.ceil(days / 7)),
        };
      })
      .filter(Boolean)
      // แถวที่ผู้ใช้ลากจัดลำดับไว้มาก่อน ที่เหลือเรียงตามวันเริ่ม–วันสิ้นสุด
      .sort((a, b) => {
        const ia = pfOrder.indexOf(a.id);
        const ib = pfOrder.indexOf(b.id);
        return (ia === -1 ? 9999 : ia) - (ib === -1 ? 9999 : ib) || a.start - b.start || a.end - b.end;
      });

    if (!rows.length) return null;

    const todayDate = toDate(today);
    const minStart = new Date(Math.min(...rows.map((r) => r.start)));
    const maxEnd = new Date(Math.max(...rows.map((r) => r.end)));
    // ขยายแกนเวลาให้ครอบคลุม "วันนี้" เสมอ เพื่อให้เส้นวันนี้อยู่ในภาพ
    const months = buildMonthCells(new Date(Math.min(minStart, todayDate)), new Date(Math.max(maxEnd, todayDate)));

    // จำนวนโปรเจกต์ที่รันพร้อมกันในแต่ละเดือน
    const load = months.map((m) => rows.filter((r) => r.start <= m.end && r.end >= m.start).length);
    const peak = Math.max(...load, 0);
    const peakMonths = months.filter((_, i) => load[i] === peak && peak > 0).map((m) => m.label);
    // เดือนแรกจากวันนี้ไปที่ไม่มีงานค้างเลย = ช่วงที่รับงานใหม่ได้สบาย
    const nextFreeIdx = months.findIndex((m, i) => load[i] === 0 && m.end >= todayDate);
    const nextFree = nextFreeIdx === -1 ? null : months[nextFreeIdx].label;

    // งานที่กำลังรันอยู่ ณ วันนี้ (ไม่นับที่ยังไม่เริ่ม แม้ช่วงวันจะคร่อมวันนี้)
    const runningNow = rows.filter((r) => r.status !== PROJECT_STATUS_NOT_STARTED && r.start <= todayDate && r.end >= todayDate);

    // ตำแหน่งบนแกนเวลา (หน่วย = เดือน) ไว้คูณกับความกว้างช่องเดือนตอน render
    rows.forEach((r) => {
      r.posStart = monthPosStart(months, r.start);
      r.posEnd = monthPosEnd(months, r.end);
      r.startsIn = r.start > todayDate ? daysBetween(todayDate, r.start) : null; // ยังไม่ถึงวันเริ่ม
    });
    const upcoming = rows.filter((r) => r.status === PROJECT_STATUS_NOT_STARTED);
    const todayPos = monthPosStart(months, todayDate);

    return { rows, months, load, peak, peakMonths, nextFree, runningNow, upcoming, todayDate, todayPos };
  }, [projects, activeProjectList, includeDoneInOverview, pfOrder, today]);

  // ช่วงเวลาที่ผู้ใช้กำหนดเอง (ว่าง = คำนวณอัตโนมัติจากวันงาน)
  const [rangeStart, setRangeStart] = useState(""); // "YYYY-MM"
  const [rangeEnd, setRangeEnd] = useState("");

  // โหลดช่วงเวลาที่บันทึกไว้ของโปรเจกต์ที่เลือก
  useEffect(() => {
    const p = projects.find((x) => x.id === selectedProjectId);
    setRangeStart(p?.range_start || "");
    setRangeEnd(p?.range_end || "");
  }, [selectedProjectId, projects]);

  const [autoStart, autoEnd] = useMemo(() => getDateRange(tasks), [tasks]);
  const timelineStart = useMemo(() => (rangeStart ? startOfMonth(rangeStart) : autoStart), [rangeStart, autoStart]);
  const timelineEnd = useMemo(() => {
    if (!rangeEnd) return autoEnd;
    const end = endOfMonth(rangeEnd);
    return end < timelineStart ? endOfMonth(monthStr(timelineStart)) : end; // กันกรณีเลือกสิ้นสุดก่อนเริ่ม
  }, [rangeEnd, autoEnd, timelineStart]);
  const weeks = useMemo(() => buildWeekLabels(timelineStart, timelineEnd), [timelineStart, timelineEnd]);

  // หา index สัปดาห์ที่วันที่หนึ่ง ๆ ตกอยู่
  const weekIndexOf = useCallback(
    (iso) => {
      const d = toDate(iso);
      const i = weeks.findIndex((w) => d >= w.start && d <= w.end);
      if (i !== -1) return i;
      if (d < weeks[0]?.start) return 0;
      return weeks.length - 1;
    },
    [weeks]
  );

  const currentWeekIndex = useMemo(() => {
    const d = toDate(today);
    return weeks.findIndex((w) => d >= w.start && d <= w.end);
  }, [weeks, today]);

  // จัดกลุ่มสัปดาห์ตามเดือนเพื่อทำ header เดือน
  const monthGroups = useMemo(() => {
    const groups = [];
    weeks.forEach((w) => {
      const friday = addDays(w.start, (5 - w.start.getDay() + 7) % 7);
      const key = `${friday.getFullYear()}-${friday.getMonth()}`;
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.span += 1;
      else groups.push({ key, label: format_date(formatISOLocal(friday), "MMM yyyy", "en"), span: 1 });
    });
    return groups;
  }, [weeks]);

  // map: index สัปดาห์ -> ลำดับเดือน (เพื่อให้แถวสัปดาห์ใช้เฉดสีของเดือนนั้น)
  const weekMonthIdx = useMemo(() => {
    const arr = [];
    monthGroups.forEach((m, gi) => {
      for (let k = 0; k < m.span; k += 1) arr.push(gi);
    });
    return arr;
  }, [monthGroups]);

  const projectTableRows = useMemo(
    () =>
      projects.map((project) => {
        const span = getProjectSpan(project);
        const todayDate = toDate(today);
        return {
          ...project,
          progress: getProjectProgress(project.tasks),
          duration: dayDiff(project.start_date, project.due_date) + 1,
          span,
          // ยังไม่ถึงวันเริ่ม = โปรเจกต์ที่ใกล้จะมาถึง
          startsInDays: span && span.start > todayDate ? daysBetween(todayDate, span.start) : null,
        };
      }),
    [projects, today]
  );

  // ลากจัดลำดับแถวในภาพรวม — เก็บลำดับไว้ฝั่ง client เท่านั้น ไม่กระทบข้อมูล/API
  const reorderPortfolio = (from, to) => {
    if (!portfolio || from === null || to === null || from === to) return;
    const ids = portfolio.rows.map((r) => r.id);
    if (from < 0 || to < 0 || from >= ids.length || to >= ids.length) return;
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    // โปรเจกต์ที่ไม่ได้แสดงอยู่ตอนนี้ (เช่นกลุ่ม Done) คงลำดับเดิมไว้ท้ายแถว
    const rest = pfOrder.filter((id) => !ids.includes(id));
    setPfOrder([...ids, ...rest]);
  };

  // จำลำดับที่จัดไว้ข้ามการรีเฟรชหน้า
  useEffect(() => {
    try {
      if (pfOrder.length) localStorage.setItem(PF_ORDER_KEY, JSON.stringify(pfOrder));
      else localStorage.removeItem(PF_ORDER_KEY);
    } catch {
      // เขียน localStorage ไม่ได้ก็ข้ามไป ไม่ต้องรบกวนผู้ใช้
    }
  }, [pfOrder]);

  // แยกการ์ดโปรเจกต์: กำลังดำเนินการ / เสร็จแล้ว
  const projectCards = useMemo(
    () => ({
      active: projectTableRows.filter((p) => p.status !== "Done" && p.status !== PROJECT_STATUS_NOT_STARTED),
      upcoming: projectTableRows
        .filter((p) => p.status === PROJECT_STATUS_NOT_STARTED)
        // ที่มีวันเริ่มแล้วขึ้นก่อน เรียงจากที่จะถึงเร็วสุด ส่วนที่ยังไม่กำหนดวันไว้ท้ายสุด
        .sort((a, b) => (a.startsInDays ?? 99999) - (b.startsInDays ?? 99999)),
      done: projectTableRows.filter((p) => p.status === "Done"),
    }),
    [projectTableRows]
  );

  // อัปเดตสถานะของ task ในแถวได้ทันที (ปรับ progress อัตโนมัติตามสถานะ)
  const updateTaskStatus = async (taskId, status) => {
    const progress = status === "Done" ? 100 : status === "Planned" ? 0 : undefined;
    loading(true);
    const res = await PUT(`${API_TASKS}/${taskId}`, { status, ...(progress !== undefined ? { progress } : {}) });
    loading(false);
    if (res?.status) {
      await getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", res?.message || "อัปเดตสถานะไม่สำเร็จ");
    }
  };

  // อัปเดตชื่อ task แบบ inline
  const updateTaskName = async (taskId, name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    loading(true);
    const res = await PUT(`${API_TASKS}/${taskId}`, { name: trimmed });
    loading(false);
    if (res?.status) await getData();
    else await alert("", "warning", "แจ้งเตือน", res?.message || "แก้ไขชื่อไม่สำเร็จ");
  };

  // จัดลำดับ task ใหม่ด้วยการลากวาง (ย้ายจาก index from -> to) แล้วบันทึก order ใหม่
  const reorderTasks = async (from, to) => {
    if (from === to || from < 0 || to < 0 || to >= visibleTasks.length) return;
    const arr = [...visibleTasks];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    const changed = [];
    arr.forEach((t, k) => {
      const newOrder = String(k + 1);
      if (t.order !== newOrder) changed.push({ id: t.id, order: newOrder });
    });
    if (!changed.length) return;
    loading(true);
    await Promise.all(changed.map((c) => PUT(`${API_TASKS}/${c.id}`, { order: c.order })));
    loading(false);
    await getData();
  };

  // บันทึกช่วงเวลา + จำนวนสัปดาห์ ของโปรเจกต์
  const saveRange = async () => {
    if (!selectedProject) return;
    const rs = rangeStart || monthStr(autoStart);
    const re = rangeEnd || monthStr(autoEnd);
    loading(true);
    const res = await PUT(`${API_PROJECTS}/${selectedProject.id}`, { range_start: rs, range_end: re, total_weeks: weeks.length });
    loading(false);
    if (res?.status) {
      await alert("", "success", "สำเร็จ", "บันทึกช่วงเวลาแล้ว");
      await getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", res?.message || "บันทึกช่วงเวลาไม่สำเร็จ");
    }
  };

  // ---- สร้าง/แก้ไขโปรเจกต์ ----
  const openProjectModal = () => setProjectForm({ editId: null, key: "", name: "", members: [], status: "In Progress", platform: "", total_price: "", installments: [] });
  const openEditProject = () => {
    if (!selectedProject) return;
    setProjectForm({
      editId: selectedProject.id,
      key: selectedProject.key,
      name: selectedProject.name,
      members: (selectedProject.members || []).map((m) => ({ ...m })),
      status: selectedProject.status || "In Progress",
      platform: selectedProject.platform || "",
      total_price: selectedProject.total_price ?? "",
      installments: (selectedProject.installments || []).map((i) => ({
        label: i.label || "",
        percent: i.percent ?? "",
        is_paid: !!i.is_paid,
        paid_date: i.paid_date || "",
        note: i.note || "",
      })),
    });
  };
  const closeProjectModal = () => setProjectForm(null);

  // ---- แก้ไขผู้รับผิดชอบในฟอร์ม ----
  const updateMember = (idx, patch) =>
    setProjectForm((c) => ({ ...c, members: c.members.map((m, n) => (n === idx ? { ...m, ...patch } : m)) }));
  const addMember = () => setProjectForm((c) => ({ ...c, members: [...c.members, { employee_uid: "", role: c.members.length ? "tester" : "owner" }] }));
  const removeMember = (idx) => setProjectForm((c) => ({ ...c, members: c.members.filter((_, n) => n !== idx) }));

  // ---- แก้ไขงวดชำระเงินในฟอร์ม (super_user) ----
  const updateInstallment = (idx, patch) =>
    setProjectForm((c) => ({ ...c, installments: c.installments.map((i, n) => (n === idx ? { ...i, ...patch } : i)) }));
  const addInstallment = () => setProjectForm((c) => ({ ...c, installments: [...c.installments, newInstallment(c.installments.length + 1)] }));
  const removeInstallment = (idx) =>
    setProjectForm((c) => ({
      ...c,
      installments: c.installments
        .filter((_, n) => n !== idx)
        // งวดที่ยังใช้ชื่อมาตรฐาน "งวดที่ n" ให้เลื่อนเลขตามลำดับใหม่ ชื่อที่ผู้ใช้ตั้งเองคงไว้
        .map((i, n) => (/^งวดที่ \d+$/.test(i.label) ? { ...i, label: `งวดที่ ${n + 1}` } : i)),
    }));
  const applyInstallmentPreset = async (parts) => {
    const hasPaid = projectForm.installments.some((i) => i.is_paid);
    if (projectForm.installments.length && !(await alert("", "warning", "แทนที่งวดชำระเงิน", hasPaid ? "งวดเดิมมีรายการที่ชำระแล้ว ต้องการแทนที่ด้วยรูปแบบใหม่ทั้งหมดใช่หรือไม่?" : "ต้องการแทนที่งวดเดิมทั้งหมดใช่หรือไม่?", "แทนที่", "ยกเลิก"))) return;
    setProjectForm((c) => ({ ...c, installments: parts.map((pct, n) => newInstallment(n + 1, pct)) }));
  };

  // แปลงงวดในฟอร์มเป็น payload ส่ง API (amount ให้ backend คำนวณจาก percent)
  const buildInstallmentsPayload = (installments) =>
    installments.map((i, n) => ({
      no: n + 1,
      label: i.label?.trim() || `งวดที่ ${n + 1}`,
      percent: round2(i.percent),
      is_paid: !!i.is_paid,
      paid_date: i.is_paid && i.paid_date ? i.paid_date : null,
      note: i.note?.trim() || null,
    }));

  const saveProject = async () => {
    if (!projectForm.key.trim() || !projectForm.name.trim()) {
      await alert("", "warning", "แจ้งเตือน", "กรุณากรอก Project Key และชื่อโปรเจกต์");
      return;
    }
    const code = projectForm.key.trim().toUpperCase();
    const body = {
      project_code: code,
      name: projectForm.name.trim(),
      status: projectForm.status,
      platform: projectForm.platform || "",
      // แถวที่ยังไม่ได้เลือกพนักงานไม่ส่ง · ช่อง owner (ข้อความเดิม) ไม่ส่งแล้ว ข้อมูลเดิมคงอยู่ใน DB
      members: (projectForm.members || []).filter((m) => m.employee_uid).map((m) => ({ employee_uid: m.employee_uid, role: m.role })),
    };
    if (IS_SUPER_USER) {
      const installments = projectForm.installments || [];
      if (installments.some((i) => i.percent === "" || Number.isNaN(Number(i.percent)))) {
        await alert("", "warning", "แจ้งเตือน", "กรุณากรอก % ของทุกงวดชำระเงิน");
        return;
      }
      const pct = sumPercent(installments);
      if (pct > 100) {
        await alert("", "warning", "แจ้งเตือน", `% รวมของงวดชำระเงินต้องไม่เกิน 100% (ตอนนี้ ${pct}%)`);
        return;
      }
      if (installments.length && pct < 100 && !(await alert("", "warning", "แจ้งเตือน", `% รวมของงวดชำระเงินยังไม่ครบ 100% (ตอนนี้ ${pct}%) ต้องการบันทึกต่อหรือไม่?`, "บันทึก", "ยกเลิก"))) return;
      body.total_price = projectForm.total_price === "" || projectForm.total_price === null ? null : round2(projectForm.total_price);
      body.payment_installments = buildInstallmentsPayload(installments);
    }
    loading(true);
    const res = projectForm.editId
      ? await PUT(`${API_PROJECTS}/${projectForm.editId}`, body)
      : await POST(API_PROJECTS, body);
    loading(false);
    if (res?.status) {
      closeProjectModal();
      await alert("", "success", "สำเร็จ", projectForm.editId ? "บันทึกโปรเจกต์สำเร็จ" : "สร้างโปรเจกต์สำเร็จ");
      await getData(code);
    } else {
      await alert("", "warning", "แจ้งเตือน", res?.message || "บันทึกโปรเจกต์ไม่สำเร็จ");
    }
  };

  // ---- สลับสถานะชำระของงวดจากแถบสรุป (super_user) ----
  const togglePaidStatus = async (idx) => {
    if (!selectedProject || !IS_SUPER_USER) return;
    const current = selectedProject.installments || [];
    const target = current[idx];
    if (!target) return;
    const nextPaid = !target.is_paid;
    const title = nextPaid ? "ยืนยันการชำระเงิน" : "ยกเลิกสถานะชำระแล้ว";
    const detail = nextPaid
      ? `บันทึกว่า "${target.label}" (${fmtMoney(target.amount)} บาท) ชำระแล้ววันนี้ใช่หรือไม่?`
      : `ต้องการเปลี่ยน "${target.label}" กลับเป็นยังไม่ชำระใช่หรือไม่?`;
    if (!(await alert("", "question", title, detail, "ยืนยัน", "ยกเลิก"))) return;
    const payment_installments = current.map((i, n) => ({
      no: n + 1,
      label: i.label,
      percent: round2(i.percent),
      is_paid: n === idx ? nextPaid : !!i.is_paid,
      paid_date: n === idx ? (nextPaid ? todayIso() : null) : i.paid_date || null,
      note: i.note || null,
    }));
    loading(true);
    const res = await PUT(`${API_PROJECTS}/${selectedProject.id}`, { payment_installments });
    loading(false);
    if (res?.status) {
      await getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", res?.message || "บันทึกสถานะการชำระไม่สำเร็จ");
    }
  };

  // ---- ลบโปรเจกต์ (ลบ tasks ทั้งหมดในโปรเจกต์ไปด้วย) ----
  const deleteProject = async () => {
    if (!projectForm?.editId) return;
    const taskCount = selectedProject?.tasks?.length || 0;
    const detail = taskCount
      ? `ต้องการลบโปรเจกต์ "${projectForm.name}" ใช่หรือไม่? งานทั้งหมด ${taskCount} รายการในโปรเจกต์นี้จะถูกลบไปด้วย`
      : `ต้องการลบโปรเจกต์ "${projectForm.name}" ใช่หรือไม่?`;
    if (!(await alert("", "warning", "ลบโปรเจกต์", detail, "ลบ", "ยกเลิก"))) return;
    loading(true);
    const res = await DELETE(`${API_PROJECTS}/${projectForm.editId}`, null);
    loading(false);
    if (res?.status) {
      closeProjectModal();
      setSelectedTaskId(null);
      await alert("", "success", "สำเร็จ", "ลบโปรเจกต์สำเร็จ");
      await getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", res?.message || "ลบโปรเจกต์ไม่สำเร็จ");
    }
  };

  // วิเคราะห์ทุกโปรเจกต์แล้วแสดงผล
  const runAnalysis = () => {
    if (!projects.length) return;
    setAnalysis(analyzeProjects(projects, today));
  };

  const openTaskModal = async (task = null) => {
    if (!task && !selectedProject) {
      await alert("", "warning", "แจ้งเตือน", "กรุณาสร้างหรือเลือกโปรเจกต์ก่อน");
      return;
    }
    setModalMode(task ? "edit" : "create");
    setModalTask(task ? { ...task } : createNewTask(null));
    setShowModal(true);
  };

  const closeTaskModal = () => {
    setShowModal(false);
    setModalTask(null);
  };

  const onTaskSave = async () => {
    if (!modalTask) return;
    if (!modalTask.name.trim()) {
      await alert("", "warning", "แจ้งเตือน", "กรุณากรอกชื่อ Issue");
      return;
    }
    if (!modalTask.start_date || !modalTask.due_date) {
      await alert("", "warning", "แจ้งเตือน", "กรุณากรอกวันที่เริ่มต้นและวันที่สิ้นสุด");
      return;
    }
    const payload = {
      name: modalTask.name.trim(),
      type: modalTask.type,
      start_date: modalTask.start_date,
      due_date: modalTask.due_date,
      status: modalTask.status,
      assignee: modalTask.assignee,
      priority: modalTask.priority,
      progress: Number(modalTask.progress) || 0,
      parent_id: modalTask.parentId || null,
    };
    loading(true);
    const res =
      modalMode === "edit"
        ? await PUT(`${API_TASKS}/${modalTask.id}`, payload)
        : await POST(API_TASKS, { project_id: selectedProject.id, order: modalTask.order || "", ...payload });
    loading(false);
    if (res?.status) {
      closeTaskModal();
      await alert("", "success", "สำเร็จ", "บันทึกงานสำเร็จ");
      await getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", res?.message || "บันทึกงานไม่สำเร็จ");
    }
  };

  // style สำหรับคอลัมน์ซ้ายที่ปักหมุด (sticky)
  const stickyCell = (key, { header = false } = {}) => {
    const col = cols.find((c) => c.key === key);
    return {
      position: "sticky",
      left: COL_LEFT[key],
      minWidth: col.width,
      width: col.width,
      background: header ? HEADER_NAVY : "#ffffff",
      zIndex: header ? 6 : 3,
      boxShadow: key === "status" ? "2px 0 0 rgba(15, 62, 138, 0.18)" : undefined,
    };
  };

  // เนื้อในของแต่ละคอลัมน์ปักหมุด — แยกออกมาเพื่อให้ชุดคอลัมน์สลับได้ตามขนาดจอ
  const renderStickyCell = (col, task, idx, { isSelected, stickyBg, wkSpan }) => {
    const base = { ...stickyCell(col.key), borderBottom: "1px solid #eef2f7", background: stickyBg };

    switch (col.key) {
      case "no":
        return (
          <td
            key={col.key}
            draggable={!isMobile}
            title={isMobile ? undefined : "ลากเพื่อจัดลำดับ"}
            onDragStart={(ev) => { setDragIndex(idx); ev.dataTransfer.effectAllowed = "move"; }}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
            style={{ ...base, padding: isMobile ? "1px 2px" : "2px 4px", cursor: isMobile ? "pointer" : "grab", userSelect: "none", borderLeft: isSelected ? `3px solid ${BRAND_BLUE}` : undefined }}
          >
            <div className="d-flex align-items-center justify-content-center gap-1">
              {/* จับลากบนมือถือไม่ถนัดอยู่แล้ว ซ่อนหูจับไว้เอาที่ให้ตัวเลข */}
              {!isMobile && <i className="fa-solid fa-grip-vertical" style={{ color: "#94a3b8", fontSize: 11 }} />}
              <span className="fw-semibold" style={{ color: BRAND_BLUE, fontSize: isMobile ? 10.5 : 12 }}>{idx + 1}</span>
            </div>
          </td>
        );
      case "task":
        // มือถือ: ข้อความล้วน ไม่ใช่ input — style.css บังคับช่องกรอกเป็น 16px จะทำให้แถวสูงเกิน
        // แตะแถวเพื่อเปิดโมดัลแก้ไขแทน สถานะย่อเป็นจุดสีหน้าชื่อ
        if (isMobile) {
          return (
            <td key={col.key} style={{ ...base, padding: "2px 6px", lineHeight: 1.25 }}>
              <div className="d-flex align-items-center gap-1">
                <span
                  style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: statusBadge[task.status]?.color || "#94a3b8" }}
                  title={task.status}
                />
                <span className="fw-semibold text-truncate" style={{ fontSize: 11 }}>{task.name}</span>
              </div>
              <div className="text-secondary text-truncate" style={{ fontSize: 8.5, paddingLeft: 10 }}>
                {format_date(task.start_date, "dd/mm/yy")} – {format_date(task.due_date, "dd/mm/yy")} · {wkSpan}w
              </div>
            </td>
          );
        }
        return (
          <td key={col.key} style={{ ...base, padding: "2px 8px" }}>
            <input
              key={task.name}
              defaultValue={task.name}
              title="แก้ไขชื่อแล้วคลิกออก/กด Enter เพื่อบันทึก"
              className="fw-semibold w-100"
              onClick={(ev) => ev.stopPropagation()}
              onDoubleClick={(ev) => ev.stopPropagation()}
              onKeyDown={(ev) => { if (ev.key === "Enter") ev.target.blur(); }}
              onBlur={(ev) => {
                ev.target.style.border = "1px solid transparent";
                ev.target.style.background = "transparent";
                if (ev.target.value.trim() && ev.target.value.trim() !== task.name) updateTaskName(task.id, ev.target.value);
              }}
              style={{ fontSize: 12.5, border: "1px solid transparent", borderRadius: 6, background: "transparent", padding: "1px 4px", outline: "none" }}
              onFocus={(ev) => { ev.target.style.border = "1px solid #cbd5e1"; ev.target.style.background = "#fff"; }}
              onMouseDown={(ev) => ev.stopPropagation()}
            />
          </td>
        );
      case "start":
        return (
          <td key={col.key} style={{ ...base, textAlign: "center", padding: "2px 4px", color: "#475569", fontSize: 11.5 }}>
            {format_date(task.start_date, "dd/mm/yyyy")}
          </td>
        );
      case "due":
        return (
          <td key={col.key} style={{ ...base, textAlign: "center", padding: "2px 4px", color: "#475569", fontSize: 11.5 }}>
            {format_date(task.due_date, "dd/mm/yyyy")}
          </td>
        );
      case "wks":
        return (
          <td key={col.key} style={{ ...base, textAlign: "center", padding: "2px 4px", fontSize: 12 }}>{wkSpan}</td>
        );
      case "status":
        return (
          <td key={col.key} style={{ ...base, padding: "2px 4px" }}>
            <select
              className="form-select form-select-sm fw-semibold"
              value={task.status}
              onClick={(ev) => ev.stopPropagation()}
              onChange={(ev) => updateTaskStatus(task.id, ev.target.value)}
              title="คลิกเพื่อเปลี่ยนสถานะ"
              style={{
                color: statusBadge[task.status]?.color,
                backgroundColor: statusBadge[task.status]?.background,
                fontSize: 10,
                lineHeight: 1.1,
                border: "none",
                borderRadius: 999,
                padding: "2px 16px 2px 8px",
                height: 22,
                cursor: "pointer",
                width: "100%",
                backgroundPosition: "right 4px center",
                backgroundSize: "9px 7px",
              }}
            >
              {statusOptions.map((s) => (
                <option key={s} value={s} style={{ color: "#1e293b", background: "#fff" }}>{s}</option>
              ))}
            </select>
          </td>
        );
      default:
        return null;
    }
  };

  // การ์ดเลือกโปรเจกต์ — ใช้ทั้งกลุ่มที่กำลังทำและกลุ่มที่เสร็จแล้ว (dimmed = กลุ่ม Done)
  const renderProjectCard = (project, dimmed = false) => {
    const active = project.id === selectedProjectId;
    return (
      <button
        key={project.id}
        type="button"
        onClick={() => setSelectedProjectId(project.id)}
        className="text-start p-2 px-3"
        style={{
          width: 210,
          borderRadius: 10,
          border: active ? `1.5px solid ${BRAND_BLUE}` : "1px solid #e2e8f0",
          background: active ? "#eaf3ff" : "#ffffff",
          boxShadow: active ? "0 4px 12px rgba(15, 107, 220, 0.18)" : "0 1px 3px rgba(15, 23, 42, 0.04)",
          transition: "all 0.12s ease",
          opacity: dimmed && !active ? 0.72 : 1,
        }}
      >
        {/* ชื่อโปรเจกต์ขึ้นก่อน แล้วค่อย key — ให้เหมือนคอลัมน์ในภาพรวม */}
        <div className="d-flex justify-content-between align-items-center gap-1">
          <span className="fw-bold text-truncate" style={{ fontSize: 13, color: active ? "#0b4fa0" : "#1e293b" }}>{project.name}</span>
          <span className="rounded-pill px-2 flex-shrink-0" style={{ background: "rgba(15,107,220,0.12)", color: BRAND_BLUE, fontSize: 10, fontWeight: 600 }}>{project.progress}%</span>
        </div>
        <div className="d-flex align-items-center gap-1" style={{ minWidth: 0 }}>
          <span className="text-secondary text-truncate" style={{ fontSize: 10.5 }}>{project.key}</span>
          {renderPlatformTag(project.platform, { fontSize: 9.5 })}
        </div>
        {project.members?.length > 0 && (
          <div className="d-flex flex-wrap gap-1 mt-1" style={{ minWidth: 0 }}>{renderMemberTags(project.members, { fontSize: 9.5 })}</div>
        )}
        <div className="d-flex align-items-center justify-content-end gap-1 mt-1">
          <span className="rounded-pill px-2 d-inline-flex align-items-center gap-1 flex-shrink-0" style={{ ...statusBadge[project.status], fontSize: 10, fontWeight: 600 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusBadge[project.status]?.color, display: "inline-block" }} />
            {projectStatusLabel(project.status)}
          </span>
        </div>
        <div className="rounded-pill mt-1" style={{ height: 4, background: "#e2e8f0", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${project.progress}%`, background: barGradient(statusBarColor[project.status] || statusBarColor["In Progress"]) }} />
        </div>
        {project.status === PROJECT_STATUS_NOT_STARTED && (
          <div className="text-truncate mt-1" style={{ fontSize: 10, color: "#b45309" }}>
            <i className="fa-regular fa-clock me-1" />
            {project.span
              ? `เริ่ม ${format_date(formatISOLocal(project.span.start), "dd/mm/yy")}${project.startsInDays ? ` · อีก ${project.startsInDays} วัน` : ""}`
              : "ยังไม่กำหนดวันเริ่ม"}
          </div>
        )}
      </button>
    );
  };

  // เปิดกลุ่มอัตโนมัติ ถ้าโปรเจกต์ที่เลือกอยู่ในกลุ่มนั้น
  const activeListOpen = showActiveList || projectCards.active.some((p) => p.id === selectedProjectId);
  const doneListOpen = showDoneList || projectCards.done.some((p) => p.id === selectedProjectId);
  const upcomingListOpen = showUpcomingList || projectCards.upcoming.some((p) => p.id === selectedProjectId);

  // กลุ่มโปรเจกต์ — ย่อ/กางได้เหมือนกันทั้ง 3 กลุ่ม และแสดงหัวกลุ่มตลอดแม้ยังไม่มีโปรเจกต์ในกลุ่ม
  const renderProjectGroup = ({ icon, iconColor, title, items, open, onToggle, badgeBg, badgeColor, note = null, dimmed = false, emptyText, first = false }) => (
    <div className={first ? "" : "mt-2 pt-2"} style={first ? undefined : { borderTop: "1px dashed #cbd5e1" }}>
      <button
        type="button"
        className="btn btn-sm d-inline-flex align-items-center gap-2 fw-semibold"
        style={{ fontSize: 12, borderRadius: 8, background: "#ffffff", border: "1px solid #e2e8f0", color: "#475569" }}
        onClick={onToggle}
      >
        <i className={`fa-solid fa-chevron-${open ? "down" : "right"}`} style={{ fontSize: 10 }} />
        <i className={icon} style={{ color: iconColor }} />
        {title}
        <span className="rounded-pill px-2" style={{ background: badgeBg, color: badgeColor, fontSize: 10.5 }}>{items.length}</span>
        {note}
      </button>
      {open && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {items.length ? (
            items.map((project) => renderProjectCard(project, dimmed))
          ) : (
            <span className="text-secondary" style={{ fontSize: 12 }}>{emptyText}</span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <AppShell title="Project Timeline" description="แผนงานโปรเจกต์แบบ Gantt รายสัปดาห์ จัดกลุ่มตามเดือน">
      {/* ---- Summary cards (compact) ---- */}
      <div className="row g-3 mb-3">
        {[
          { icon: "fa-solid fa-rocket", label: "Active Projects", value: summaryValues.activeProjects, accent: BRAND_BLUE, soft: "rgba(15, 107, 220, 0.12)" },
          { icon: "fa-solid fa-triangle-exclamation", label: "Overdue Tasks", value: summaryValues.overdueTasks, accent: "#f59e0b", soft: "rgba(245, 158, 11, 0.12)" },
          { icon: "fa-solid fa-chart-line", label: "Overall Performance", value: `${summaryValues.overallPerformance}%`, accent: "#10b981", soft: "rgba(16, 185, 129, 0.12)" },
          { icon: "fa-solid fa-circle-check", label: "Done Projects", value: summaryValues.doneProjects, accent: "#64748b", soft: "rgba(100, 116, 139, 0.12)" },
        ].map((card) => (
          <div className="col-6 col-md-3" key={card.label}>
            <div className="summary-card rounded-3 p-2 p-md-3 h-100 d-flex align-items-center gap-2" style={{ background: "#ffffff", borderLeft: `3px solid ${card.accent}`, border: "1px solid #eef2f7", boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)" }}>
              <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38, borderRadius: 10, background: card.soft, fontSize: 17, color: card.accent }}>
                <i className={card.icon} />
              </div>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <p className="text-uppercase text-secondary mb-0 text-truncate" style={{ letterSpacing: 0.3, fontSize: 11 }}>{card.label}</p>
                <h4 className="fw-bold mb-0" style={{ color: card.accent, fontSize: 22 }}>{card.value}</h4>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ---- ภาพรวมระยะเวลาแต่ละโปรเจกต์ (ดูว่าไทม์ไลน์ขี่/ซ้อนกันช่วงไหน) ---- */}
      <div className="rounded-3 mb-3 overflow-hidden" style={{ background: "#ffffff", border: "1px solid #dbe4f0", boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)" }}>
        <div className="d-flex flex-wrap align-items-center gap-2 px-3 py-2" style={{ background: HEADER_TITLE_BG, borderBottom: "2px solid rgba(255,255,255,0.25)" }}>
          <span className="fw-bold text-white" style={{ fontSize: 14 }}>
            <i className="fa-solid fa-layer-group me-2" />
            ภาพรวมระยะเวลาแต่ละโปรเจกต์
          </span>
          <span className="badge rounded-pill" style={{ background: "rgba(255,255,255,0.22)", color: "#fff", fontSize: 10.5, fontWeight: 600 }}>
            {portfolio ? portfolio.rows.length : 0} โปรเจกต์
          </span>
          <button
            type="button"
            className="btn btn-sm ms-auto fw-semibold"
            style={{
              fontSize: 11,
              borderRadius: 8,
              background: includeDoneInOverview ? "#ffffff" : "rgba(255,255,255,0.16)",
              color: includeDoneInOverview ? "#0b4fa0" : "#ffffff",
              border: "1px solid rgba(255,255,255,0.45)",
            }}
            onClick={() => setIncludeDoneInOverview((v) => !v)}
          >
            <i className={`fa-solid ${includeDoneInOverview ? "fa-eye" : "fa-eye-slash"} me-1`} />
            รวมโปรเจกต์ที่เสร็จแล้ว
          </button>
          {pfOrder.length > 0 && (
            <button
              type="button"
              className="btn btn-sm fw-semibold"
              title="กลับไปเรียงตามวันเริ่มโปรเจกต์อัตโนมัติ"
              style={{ fontSize: 11, borderRadius: 8, background: "rgba(255,255,255,0.16)", color: "#ffffff", border: "1px solid rgba(255,255,255,0.45)" }}
              onClick={() => setPfOrder([])}
            >
              <i className="fa-solid fa-rotate-left me-1" />
              เรียงตามวันเริ่ม
            </button>
          )}
        </div>

        {portfolio ? (
          <>
            {/* สรุปสั้น ๆ สำหรับตัดสินใจรับงานใหม่ */}
            <div className="d-flex flex-wrap align-items-center gap-2 px-3 py-2" style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <span className="rounded-pill px-2 py-1 fw-semibold" style={{ fontSize: 10.5, background: "rgba(15,107,220,0.12)", color: "#0b4fa0" }}>
                <i className="fa-solid fa-play me-1" />รันพร้อมกันตอนนี้ {portfolio.runningNow.length} โปรเจกต์
              </span>
              {portfolio.upcoming.length > 0 && (
                <span className="rounded-pill px-2 py-1 fw-semibold" style={{ fontSize: 10.5, background: "rgba(245,158,11,0.16)", color: "#b45309" }}>
                  <i className="fa-regular fa-clock me-1" />ยังไม่เริ่ม {portfolio.upcoming.length} โปรเจกต์
                  {portfolio.upcoming.some((r) => r.startsIn !== null)
                    ? ` · เริ่มเร็วสุดอีก ${Math.min(...portfolio.upcoming.filter((r) => r.startsIn !== null).map((r) => r.startsIn))} วัน`
                    : ""}
                </span>
              )}
              <span className="rounded-pill px-2 py-1 fw-semibold" style={{ fontSize: 10.5, background: loadTone(portfolio.peak).bg, color: loadTone(portfolio.peak).color }}>
                <i className="fa-solid fa-layer-group me-1" />ซ้อนกันสูงสุด {portfolio.peak} โปรเจกต์
                {portfolio.peakMonths.length ? ` · ${portfolio.peakMonths.slice(0, 3).join(", ")}` : ""}
              </span>
              <span className="rounded-pill px-2 py-1 fw-semibold" style={{ fontSize: 10.5, background: portfolio.nextFree ? "rgba(16,185,129,0.16)" : "rgba(220,38,38,0.12)", color: portfolio.nextFree ? "#047857" : "#b91c1c" }}>
                <i className="fa-solid fa-calendar-plus me-1" />
                {portfolio.nextFree ? `ว่างรับงานใหม่: ${portfolio.nextFree}` : "ไม่มีเดือนว่างในช่วงนี้"}
              </span>
              <span className="ms-auto d-none d-md-flex align-items-center gap-2 text-secondary" style={{ fontSize: 10.5 }}>
                {projectStatusOptions.map((st) => (
                  <span key={st.value} className="d-inline-flex align-items-center gap-1">
                    <span style={{ width: 18, height: 8, borderRadius: 999, background: barGradient(statusBarColor[st.value]), display: "inline-block" }} />
                    {st.label}
                  </span>
                ))}
                <span className="d-inline-flex align-items-center gap-1">
                  <span style={{ width: 18, height: 8, borderRadius: 999, display: "inline-block", background: barGradient(statusBarColor.Planned), backgroundImage: `repeating-linear-gradient(45deg, rgba(255,255,255,0.55) 0 4px, transparent 4px 8px), ${barGradient(statusBarColor.Planned)}` }} />
                  ยังไม่เริ่ม
                </span>
                <span className="d-inline-flex align-items-center gap-1">
                  <span style={{ width: 2, height: 12, background: "rgba(220,38,38,0.7)", display: "inline-block" }} />
                  วันนี้
                </span>
              </span>
            </div>

            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 420 }}>
              <div style={{ minWidth: PF_LABEL_W + portfolio.months.length * PF_MONTH_W, width: "max-content" }}>
                {/* หัวตาราง: เดือน */}
                <div className="d-flex" style={{ position: "sticky", top: 0, zIndex: 5 }}>
                  <div
                    className="d-flex align-items-center fw-semibold"
                    style={{ position: "sticky", left: 0, zIndex: 6, width: PF_LABEL_W, minWidth: PF_LABEL_W, background: HEADER_NAVY, color: "#fff", fontSize: isMobile ? 10.5 : 12, padding: "6px 8px", borderRight: HEADER_WHITE_BORDER, borderBottom: HEADER_WHITE_BORDER }}
                  >
                    <span className="text-truncate">โปรเจกต์ / ระยะเวลา</span>
                    {!isMobile && (
                      <i className="fa-solid fa-grip-vertical ms-1" title="ลากแถวเพื่อจัดลำดับ" style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }} />
                    )}
                  </div>
                  <div className="d-flex" style={{ position: "relative" }}>
                    {portfolio.months.map((m, i) => (
                      <div
                        key={m.ym}
                        className="text-center"
                        style={{ width: PF_MONTH_W, minWidth: PF_MONTH_W, background: monthPalette[i % monthPalette.length], color: "#fff", padding: "3px 2px", borderRight: HEADER_WHITE_BORDER, borderBottom: HEADER_WHITE_BORDER, lineHeight: 1.15 }}
                      >
                        <div className="fw-bold" style={{ fontSize: isMobile ? 10 : 11.5 }}>{m.month}</div>
                        <div style={{ fontSize: 9, opacity: 0.85 }}>{m.year}</div>
                      </div>
                    ))}
                    {/* ป้าย "วันนี้" ปักบนหัวเดือนตรงตำแหน่งเส้นแดง — อยู่ในแถวหัวที่ sticky จึงเห็นตลอดแม้เลื่อนแถวลง */}
                    <span
                      className="fw-bold"
                      style={{
                        position: "absolute",
                        left: Math.min(Math.max(portfolio.todayPos * PF_MONTH_W, 20), portfolio.months.length * PF_MONTH_W - 20),
                        bottom: 2,
                        transform: "translateX(-50%)",
                        background: "#dc2626",
                        color: "#ffffff",
                        fontSize: 9,
                        lineHeight: "14px",
                        padding: "0 6px",
                        borderRadius: 999,
                        whiteSpace: "nowrap",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    >
                      วันนี้ {format_date(today, "dd/mm/yy")}
                    </span>
                  </div>
                </div>

                {/* กล่องนี้ครอบแถวโหลด + แถวโปรเจกต์ เพื่อวางเส้น "วันนี้" ทับด้านบนให้เห็นชัดตลอดแนว */}
                <div style={{ position: "relative" }}>
                  {/* เส้นวันนี้ — z-index 1 = อยู่เหนือแถบทุกโปรเจกต์ แต่ยังลอดใต้คอลัมน์ชื่อ (z-index 2) เวลาเลื่อนแนวนอน */}
                  <div
                    style={{
                      position: "absolute",
                      left: PF_LABEL_W + portfolio.todayPos * PF_MONTH_W,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      background: "#dc2626",
                      boxShadow: "0 0 0 1px rgba(255,255,255,0.65), 0 0 6px rgba(220,38,38,0.55)",
                      zIndex: 1,
                      pointerEvents: "none",
                    }}
                  >
                    {/* หัวลูกศรบอกตำแหน่งวันนี้ — เล็กพอที่จะไม่บังตัวเลขในแถวโหลดงาน */}
                    <span
                      style={{ position: "absolute", top: 0, left: 1, transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "7px solid #dc2626" }}
                    />
                  </div>
                {/* แถวความหนาแน่นของงานรายเดือน */}
                <div className="d-flex">
                  <div
                    className="d-flex align-items-center fw-semibold text-secondary"
                    style={{ position: "sticky", left: 0, zIndex: 2, width: PF_LABEL_W, minWidth: PF_LABEL_W, background: "#f8fafc", fontSize: isMobile ? 9.5 : 11, padding: "3px 8px", borderRight: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}
                  >
                    <i className="fa-solid fa-chart-simple me-1" style={{ color: BRAND_BLUE }} />
                    งานที่ซ้อนกัน / เดือน
                  </div>
                  {portfolio.months.map((m, i) => {
                    const tone = loadTone(portfolio.load[i]);
                    return (
                      <div
                        key={m.ym}
                        className="text-center fw-bold"
                        title={`${m.label} · ${portfolio.load[i]} โปรเจกต์ (${tone.label})`}
                        style={{ width: PF_MONTH_W, minWidth: PF_MONTH_W, background: tone.bg, color: tone.color, fontSize: isMobile ? 10 : 11.5, padding: "2px 0", borderRight: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}
                      >
                        {portfolio.load[i]}
                      </div>
                    );
                  })}
                </div>

                {/* แถวโปรเจกต์ — 1 แถบ = ระยะเวลาของโปรเจกต์นั้น */}
                {portfolio.rows.map((r, idx) => {
                  const isActive = r.id === selectedProjectId;
                  const left = r.posStart * PF_MONTH_W;
                  const width = Math.max(12, (r.posEnd - r.posStart) * PF_MONTH_W);
                  const bar = statusBarColor[r.status] || { from: "#cbd5e1", to: "#94a3b8" };
                  const isLate = r.status !== "Done" && r.end < portfolio.todayDate;
                  const isRunning = r.start <= portfolio.todayDate && r.end >= portfolio.todayDate;
                  const isNotStarted = r.status === PROJECT_STATUS_NOT_STARTED;
                  // เส้นน้ำเงินบอกตำแหน่งที่จะวางแถวตอนลากจัดลำดับ
                  const dropMark = pfDragIndex !== null && pfOverIndex === idx && pfDragIndex !== idx ? "inset 0 2px 0 #0f6bdc" : undefined;
                  return (
                    <div
                      key={r.id}
                      className="d-flex pf-row"
                      style={{ cursor: "pointer", opacity: pfDragIndex === idx ? 0.45 : 1 }}
                      onClick={() => setSelectedProjectId(r.id)}
                      onDragOver={(ev) => { if (pfDragIndex !== null) { ev.preventDefault(); setPfOverIndex(idx); } }}
                      onDrop={(ev) => { ev.preventDefault(); if (pfDragIndex !== null) reorderPortfolio(pfDragIndex, idx); setPfDragIndex(null); setPfOverIndex(null); }}
                      title={`${r.key} · ${r.name}\n${format_date(r.startIso, "dd/mm/yyyy")} – ${format_date(r.endIso, "dd/mm/yyyy")}\nรวม ${r.days} วัน (${r.weeks} สัปดาห์) · ${r.taskCount} งาน · คืบหน้า ${r.progress}%`}
                    >
                      <div
                        draggable={!isMobile}
                        title={isMobile ? undefined : "ลากเพื่อจัดลำดับแถว"}
                        onDragStart={(ev) => { setPfDragIndex(idx); ev.dataTransfer.effectAllowed = "move"; }}
                        onDragEnd={() => { setPfDragIndex(null); setPfOverIndex(null); }}
                        style={{ position: "sticky", left: 0, zIndex: 2, width: PF_LABEL_W, minWidth: PF_LABEL_W, height: PF_ROW_H, background: isActive ? "#eaf3ff" : "#ffffff", borderRight: "1px solid #e2e8f0", borderBottom: "1px solid #eef2f7", borderLeft: isActive ? `3px solid ${BRAND_BLUE}` : "3px solid transparent", padding: "0 8px", display: "flex", flexDirection: "column", justifyContent: "center", opacity: r.status === "Done" ? 0.7 : 1, cursor: isMobile ? "pointer" : "grab", userSelect: "none", boxShadow: dropMark }}
                      >
                        <div className="d-flex align-items-center gap-1" style={{ minWidth: 0 }}>
                          {!isMobile && <i className="fa-solid fa-grip-vertical flex-shrink-0" style={{ color: "#cbd5e1", fontSize: 10 }} />}
                          <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: statusBadge[r.status]?.color || "#94a3b8" }} />
                          <span className="fw-bold text-truncate" style={{ fontSize: isMobile ? 10.5 : 12, color: isActive ? "#0b4fa0" : "#1e293b" }}>{r.name}</span>
                          {renderPlatformTag(r.platform, { fontSize: 9, iconOnly: true })}
                        </div>
                        <div className="text-secondary text-truncate" style={{ fontSize: isMobile ? 8.5 : 9.5 }}>
                          {format_date(r.startIso, "dd/mm/yy")} – {format_date(r.endIso, "dd/mm/yy")} · {r.weeks}w
                          {isLate ? " · เลยกำหนด" : ""}
                          {isNotStarted && (
                            <span style={{ color: "#b45309" }}> · ยังไม่เริ่ม{r.startsIn ? ` (อีก ${r.startsIn} วัน)` : ""}</span>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          position: "relative",
                          height: PF_ROW_H,
                          width: portfolio.months.length * PF_MONTH_W,
                          minWidth: portfolio.months.length * PF_MONTH_W,
                          background: isActive ? "#f5f9ff" : "#ffffff",
                          backgroundImage: `repeating-linear-gradient(to right, transparent 0 ${PF_MONTH_W - 1}px, #e8eef7 ${PF_MONTH_W - 1}px ${PF_MONTH_W}px)`,
                          borderBottom: "1px solid #eef2f7",
                          boxShadow: dropMark,
                        }}
                      >
                        {/* แถบระยะเวลาของโปรเจกต์ */}
                        <div
                          style={{
                            position: "absolute",
                            left,
                            width,
                            top: 7,
                            height: PF_ROW_H - 15,
                            borderRadius: 999,
                            background: barGradient(bar),
                            border: isLate ? "1.5px solid #dc2626" : "none",
                            boxShadow: isActive ? `0 0 0 2px rgba(15,107,220,0.45)` : "0 1px 3px rgba(15,23,42,0.18)",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            opacity: r.status === "Done" ? 0.75 : 1,
                          }}
                        >
                          {/* ความคืบหน้าในแถบ */}
                          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${r.progress}%`, background: "rgba(255,255,255,0.32)" }} />
                          {/* ยังไม่เริ่ม — ทำลายทางเฉียงให้ต่างจากงานที่รันอยู่ */}
                          {isNotStarted && (
                            <div style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.38) 0 5px, transparent 5px 10px)" }} />
                          )}
                          {width > 74 && (
                            <span className="text-truncate" style={{ position: "relative", fontSize: isMobile ? 9 : 10, fontWeight: 700, color: "#fff", padding: "0 8px", textShadow: "0 1px 1px rgba(0,0,0,0.28)" }}>
                              {r.key} · {r.weeks}w · {r.progress}%
                            </span>
                          )}
                        </div>
                        {isRunning && width <= 74 && (
                          <span style={{ position: "absolute", left: left + width + 4, top: PF_ROW_H / 2 - 7, fontSize: 9, color: "#64748b" }}>{r.weeks}w</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>

          </>
        ) : (
          <div className="text-center text-secondary py-4" style={{ fontSize: 13 }}>
            <i className="fa-regular fa-calendar-xmark me-2" />
            ยังไม่มีโปรเจกต์ที่กำลังดำเนินการให้แสดงระยะเวลา
          </div>
        )}
      </div>

      {/* ---- Project selector (compact cards) ---- */}
      <div className="rounded-3 p-3 mb-3" style={{ background: "#f1f5fb", border: "1px solid #dbe4f0" }}>
        <div className="d-flex align-items-center gap-2 mb-2">
          <span style={{ width: 4, height: 16, borderRadius: 4, background: BRAND_BLUE, display: "inline-block" }} />
          <span className="fw-semibold" style={{ fontSize: 13, color: "#475569" }}>เลือกโปรเจกต์</span>
          <button
            type="button"
            className="btn btn-sm ms-auto fw-semibold text-white"
            style={{ fontSize: 12, borderRadius: 8, background: "linear-gradient(135deg, #7b3fc0, #0f6bdc)" }}
            onClick={runAnalysis}
            disabled={!projects.length}
          >
            <i className="fa-solid fa-wand-magic-sparkles me-1" /> AI วิเคราะห์
          </button>
          <button type="button" className="btn btn-sm btn-primary fw-semibold" style={{ fontSize: 12, borderRadius: 8 }} onClick={openProjectModal}>
            <i className="fa-solid fa-plus me-1" /> โปรเจกต์ใหม่
          </button>
        </div>
        {/* 3 กลุ่มใช้หัวกลุ่มแบบเดียวกัน ย่อ/กางได้หมด และแสดงตลอดแม้กลุ่มนั้นยังว่าง */}
        {renderProjectGroup({
          first: true,
          icon: "fa-solid fa-gears",
          iconColor: BRAND_BLUE,
          title: "กำลังดำเนินการ",
          items: projectCards.active,
          open: activeListOpen,
          onToggle: () => setShowActiveList((v) => !v),
          badgeBg: "rgba(15,107,220,0.12)",
          badgeColor: BRAND_BLUE,
          emptyText: "ยังไม่มีโปรเจกต์ที่กำลังดำเนินการ",
        })}

        {renderProjectGroup({
          icon: "fa-regular fa-clock",
          iconColor: "#d97706",
          title: "โปรเจกต์ที่ยังไม่เริ่ม",
          items: projectCards.upcoming,
          open: upcomingListOpen,
          onToggle: () => setShowUpcomingList((v) => !v),
          badgeBg: "rgba(245,158,11,0.16)",
          badgeColor: "#b45309",
          note: projectCards.upcoming[0]?.startsInDays ? (
            <span className="text-secondary fw-normal" style={{ fontSize: 10.5 }}>
              · เริ่มเร็วสุดอีก {projectCards.upcoming[0].startsInDays} วัน
            </span>
          ) : null,
          emptyText: "ยังไม่มีโปรเจกต์ที่รอเริ่ม",
        })}

        {renderProjectGroup({
          icon: "fa-solid fa-circle-check",
          iconColor: "#10b981",
          title: "โปรเจกต์ที่เสร็จแล้ว",
          items: projectCards.done,
          open: doneListOpen,
          onToggle: () => setShowDoneList((v) => !v),
          badgeBg: "rgba(16,185,129,0.14)",
          badgeColor: "#047857",
          dimmed: true,
          emptyText: "ยังไม่มีโปรเจกต์ที่เสร็จแล้ว",
        })}
      </div>


      {/* ---- Weekly Gantt ---- */}
      {selectedProject && (
          <div className="rounded-3 mb-4 overflow-hidden" style={{ background: "#ffffff", border: "1px solid #dbe4f0", boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)" }}>
            {/* Title bar */}
            <div className="d-flex justify-content-between align-items-center gap-2 px-3 py-2" style={{ background: HEADER_TITLE_BG, borderBottom: "2px solid rgba(255,255,255,0.25)" }}>
              <div className="text-white text-truncate d-flex align-items-center gap-2">
                <span className="fw-bold" style={{ fontSize: 15 }}>{selectedProject?.name || "— ยังไม่มีโปรเจกต์ —"}</span>
                {renderPlatformTag(selectedProject?.platform, { fontSize: 10, onDark: true })}
                <button
                  type="button"
                  title="แก้ไขข้อมูลโปรเจกต์"
                  className="btn btn-sm btn-light p-0 d-flex align-items-center justify-content-center"
                  style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0 }}
                  onClick={openEditProject}
                >
                  <i className="fa-solid fa-pen" style={{ fontSize: 11, color: "#0b4fa0" }} />
                </button>
                {renderMemberTags(selectedProject?.members, { fontSize: 10, onDark: true })}
                <span className="opacity-75 ms-1 text-truncate" style={{ fontSize: 12 }}>
                  {selectedProject?.tasks?.length || 0} tasks · {getProjectProgress(selectedProject?.tasks || [])}%
                </span>
              </div>
              <button className="btn btn-sm btn-light flex-shrink-0 fw-semibold" style={{ fontSize: 12 }} onClick={() => openTaskModal(null)}><i className="fa-solid fa-plus me-1" />Task</button>
          </div>

          {/* ---- แถบสรุปราคา/งวดชำระเงิน (เฉพาะ super_user) — กดที่งวดเพื่อสลับสถานะชำระได้เลย ---- */}
          {IS_SUPER_USER && (selectedProject.total_price !== null || (selectedProject.installments || []).length > 0) && (() => {
            const summary = selectedProject.payment_summary || {};
            const installments = selectedProject.installments || [];
            return (
              <div className="d-flex flex-wrap align-items-center gap-2 px-3 py-2" style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
                <span className="fw-semibold" style={{ fontSize: 12, color: "#78350f" }}><i className="fa-solid fa-coins me-1" /> ราคา</span>
                <span className="fw-bold" style={{ fontSize: 13, color: "#78350f" }}>{selectedProject.total_price !== null ? `${fmtMoney(selectedProject.total_price)} ฿` : "-"}</span>
                <span className="badge rounded-pill" style={{ background: "rgba(16,185,129,0.14)", color: "#047857", fontSize: 11 }}>
                  ชำระแล้ว {fmtMoney(summary.paid_amount)} ({summary.paid_count || 0}/{installments.length} งวด)
                </span>
                <span className="badge rounded-pill" style={{ background: "rgba(220,38,38,0.10)", color: "#b91c1c", fontSize: 11 }}>
                  คงเหลือ {summary.remaining_amount !== null && summary.remaining_amount !== undefined ? fmtMoney(summary.remaining_amount) : "-"}
                </span>
                <span className="vr mx-1 d-none d-md-inline" style={{ opacity: 0.2 }} />
                {installments.map((inst, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="btn btn-sm rounded-pill fw-semibold"
                    title={`${inst.label}: ${inst.percent}% = ${fmtMoney(inst.amount)} บาท${inst.is_paid ? ` · ชำระเมื่อ ${inst.paid_date || "-"}` : " · ยังไม่ชำระ (กดเพื่อบันทึกการชำระ)"}${inst.note ? ` · หมายเหตุ: ${inst.note}` : ""}`}
                    style={{
                      fontSize: 11,
                      padding: "2px 10px",
                      border: "none",
                      background: inst.is_paid ? "rgba(16,185,129,0.16)" : "#ffffff",
                      color: inst.is_paid ? "#047857" : "#475569",
                      boxShadow: inst.is_paid ? "none" : "inset 0 0 0 1px #e2e8f0",
                    }}
                    onClick={() => togglePaidStatus(idx)}
                  >
                    <i className={`me-1 ${inst.is_paid ? "fa-solid fa-circle-check" : "fa-regular fa-circle"}`} />
                    {inst.label} {inst.percent}% · {fmtMoney(inst.amount)}
                  </button>
                ))}
                {!installments.length && <span className="text-secondary" style={{ fontSize: 11 }}>ยังไม่ได้ตั้งค่างวดชำระเงิน</span>}
              </div>
            );
          })()}

          {/* ---- ตัวเลือกช่วงเวลา (เดือน/ปี เริ่ม–สิ้นสุด) ---- */}
          <div className="d-flex flex-wrap align-items-center gap-2 px-3 py-2" style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <span className="fw-semibold" style={{ fontSize: 12, color: "#475569" }}><i className="fa-solid fa-calendar-days me-1" /> ช่วงเวลา</span>
            <input
              type="month"
              className="form-control form-control-sm"
              style={{ width: 150, fontSize: 12 }}
              value={rangeStart || monthStr(autoStart)}
              max={rangeEnd || monthStr(autoEnd)}
              onChange={(e) => setRangeStart(e.target.value)}
            />
            <span className="text-secondary">–</span>
            <input
              type="month"
              className="form-control form-control-sm"
              style={{ width: 150, fontSize: 12 }}
              value={rangeEnd || monthStr(autoEnd)}
              min={rangeStart || monthStr(autoStart)}
              onChange={(e) => setRangeEnd(e.target.value)}
            />
            <span className="badge rounded-pill" style={{ background: "rgba(15,107,220,0.12)", color: BRAND_BLUE, fontSize: 11 }}>
              {weeks.length} สัปดาห์
            </span>
            <button
              type="button"
              className="btn btn-sm btn-primary fw-semibold"
              style={{ fontSize: 11, borderRadius: 8 }}
              onClick={saveRange}
              disabled={!selectedProject}
            >
              <i className="fa-solid fa-floppy-disk me-1" /> บันทึกช่วงเวลา
            </button>
            {(rangeStart || rangeEnd) && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: 11, borderRadius: 8 }}
                onClick={() => { setRangeStart(""); setRangeEnd(""); }}
              >
                <i className="fa-solid fa-rotate-left me-1" /> อัตโนมัติ
              </button>
            )}
          </div>

          {/* Gantt table */}
          <div style={{ overflowX: "auto", maxHeight: 640, overflowY: "auto" }}>
            <table style={{ borderCollapse: "separate", borderSpacing: 0, width: INFO_WIDTH + weeks.length * WEEK_W, fontSize: isMobile ? 11 : 13 }}>
              <thead>
                {/* แถวเดือน */}
                <tr>
                  {cols.map((col) => (
                    <th
                      key={col.key}
                      rowSpan={2}
                      style={{ ...stickyCell(col.key, { header: true }), top: 0, zIndex: 7, color: "#fff", textAlign: col.key === "task" ? "left" : "center", padding: isMobile ? "3px 4px" : "6px 8px", verticalAlign: "middle", fontSize: isMobile ? 10.5 : 12, borderRight: "1px solid rgba(255,255,255,0.30)", borderBottom: HEADER_WHITE_BORDER }}
                    >
                      {col.key === "no" ? (
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          {col.label}
                          <i
                            className="fa-solid fa-circle-info"
                            title="วิธีใช้การจัดลำดับ"
                            style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", cursor: "pointer", flexShrink: 0 }}
                            onClick={(e) => { e.stopPropagation(); setShowDragHelp(true); }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.9)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.55)"; }}
                          />
                        </div>
                      ) : col.key === "task" ? (
                        <div className="d-flex align-items-center gap-1">
                          {col.label}
                          <i
                            className="fa-solid fa-circle-info"
                            title="วิธีแก้ไข Task"
                            style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", cursor: "pointer", flexShrink: 0 }}
                            onClick={(e) => { e.stopPropagation(); setShowTaskHelp(true); }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.9)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.55)"; }}
                          />
                        </div>
                      ) : col.label}
                    </th>
                  ))}
                  {monthGroups.map((m, i) => (
                    <th
                      key={i}
                      colSpan={m.span}
                      style={{ position: "sticky", top: 0, zIndex: 4, background: monthPalette[i % monthPalette.length], color: "#fff", textAlign: "center", padding: isMobile ? "2px 2px" : "5px 4px", fontWeight: 700, fontSize: isMobile ? 10 : 12, letterSpacing: 0.6, borderRight: HEADER_WHITE_BORDER, borderBottom: HEADER_WHITE_BORDER, whiteSpace: "nowrap" }}
                    >
                      {m.label}
                    </th>
                  ))}
                </tr>
                {/* แถวสัปดาห์ */}
                <tr>
                  {weeks.map((w, i) => {
                    const isCurrent = i === currentWeekIndex;
                    const monthColor = monthPalette[(weekMonthIdx[i] ?? 0) % monthPalette.length];
                    return (
                      <th
                        key={w.index}
                        style={{ position: "sticky", top: HEADER_ROW_H, zIndex: 4, minWidth: WEEK_W, width: WEEK_W, background: isCurrent ? WEEK_TODAY_BG : lighten(monthColor), color: "#fff", textAlign: "center", padding: "3px 2px", borderRight: HEADER_WHITE_BORDER, borderBottom: HEADER_WHITE_BORDER, lineHeight: 1.2, fontWeight: isCurrent ? 700 : 400, textShadow: isCurrent ? "0 1px 2px rgba(0,0,0,0.3)" : undefined }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 11 }}>{w.label}</div>
                        <div style={{ fontSize: 9, opacity: 0.9 }}>{format_date(formatISOLocal(addDays(w.start, (5 - w.start.getDay() + 7) % 7)), "dd/mm")}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleTasks.map((task, idx) => {
                  const s = weekIndexOf(task.start_date);
                  const e = weekIndexOf(task.due_date);
                  const wkSpan = e - s + 1;
                  const barColor = statusBarColor[task.status] || { from: "#cbd5e1", to: "#94a3b8" };
                  const isOverdue = task.status !== "Done" && toDate(task.due_date) < toDate(today);
                  const isSelected = selectedTaskId === task.id;
                  const stickyBg = isSelected ? "#dbeafe" : isOverdue ? "#fdedad" : "#ffffff";
                  return (
                    <tr
                      key={task.id}
                      style={{ cursor: "pointer", borderTop: overIndex === idx && dragIndex !== null && dragIndex !== idx ? "2px solid #0f6bdc" : undefined, opacity: dragIndex === idx ? 0.4 : 1 }}
                      // มือถือแตะครั้งเดียวเปิดโมดัลเลย เพราะแก้ไขในตารางไม่ได้แล้ว
                      onClick={() => (isMobile ? openTaskModal(task) : setSelectedTaskId(isSelected ? null : task.id))}
                      onDoubleClick={() => openTaskModal(task)}
                      className={`gantt-row${isSelected ? " gantt-row-selected" : ""}`}
                      onDragOver={(e) => { if (dragIndex !== null) { e.preventDefault(); setOverIndex(idx); } }}
                      onDrop={(e) => { e.preventDefault(); if (dragIndex !== null) reorderTasks(dragIndex, idx); setDragIndex(null); setOverIndex(null); }}
                    >
                      {cols.map((col) => renderStickyCell(col, task, idx, { isSelected, stickyBg, wkSpan }))}
                      {weeks.map((w, i) => {
                        const inBar = i >= s && i <= e;
                        const isCurrent = i === currentWeekIndex;
                        let segColor = barColor;
                        if (inBar && isCurrent && task.status === "In Progress") segColor = TODAY_ACCENT;
                        const isEnd = i === e;
                        // ลงสีเต็มช่อง: ถ้าอยู่ในช่วงงาน = สีสถานะ, วันปัจจุบันที่ว่าง = แดงจาง, อื่น ๆ = พื้นเทาอ่อน
                        const cellBg = inBar ? barGradient(segColor) : isCurrent ? "rgba(239, 68, 68, 0.10)" : isSelected ? "#dbeafe" : "#f3f6fb";
                        return (
                          <td
                            key={w.index}
                            title={inBar ? `${task.name} · ${task.status} · ${task.progress}%` : undefined}
                            style={{
                              minWidth: WEEK_W,
                              width: WEEK_W,
                              padding: 0,
                              textAlign: "center",
                              background: cellBg,
                              borderRight: HEADER_WHITE_BORDER,
                              borderBottom: HEADER_WHITE_BORDER,
                              boxShadow: isCurrent ? "inset 2px 0 0 rgba(220, 38, 38, 0.7), inset -2px 0 0 rgba(220, 38, 38, 0.7)" : undefined,
                            }}
                          >
                            {inBar && isEnd && wkSpan > 1 && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", textShadow: "0 1px 1px rgba(0,0,0,0.3)" }}>{task.progress}%</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-3 px-md-4 py-2 small text-secondary">{isMobile ? "แตะที่แถวงานเพื่อแก้ไขรายละเอียด · จุดสีหน้าชื่อคือสถานะ" : "ดับเบิลคลิกที่แถวงานเพื่อแก้ไขรายละเอียด"}</div>
        </div>
      )}

      {/* ---- Create / Edit modal ---- */}
      <Modal show={showModal} onHide={closeTaskModal} centered size="lg" contentClassName="border-0 shadow-lg overflow-hidden">
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, rgba(12, 118, 229, 0.20), rgba(47, 213, 228, 0.18))" }}>
          <Modal.Title className="fw-bold">[{selectedProject?.key}] - {modalMode === "edit" ? "Update Task" : "Create Task"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 px-md-5 py-4">
          {modalTask && (
            <div className="row g-4">
              <div className="col-md-6">
                <label className="form-label">Type {required}</label>
                <select className="form-select" value={modalTask.type} onChange={(e) => setModalTask((c) => ({ ...c, type: e.target.value }))}>
                  {issueTypeOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              {/* <div className="col-md-6">
                <label className="form-label">Parent Link</label>
                <select className="form-select" value={modalTask.parentId || ""} onChange={(e) => setModalTask((c) => ({ ...c, parentId: e.target.value || null }))} disabled={modalTask.type !== "Sub-task"}>
                  <option value="">Select parent</option>
                  {getParentOptions(tasks).map((o) => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
              </div> */}
              <div className="col-6">
                <label className="form-label">Task Name {required}</label>
                <input className="form-control" value={modalTask.name} onChange={(e) => setModalTask((c) => ({ ...c, name: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Assignee</label>
                <input className="form-control" value={modalTask.assignee} onChange={(e) => setModalTask((c) => ({ ...c, assignee: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Priority</label>
                <select className="form-select" value={modalTask.priority} onChange={(e) => setModalTask((c) => ({ ...c, priority: e.target.value }))}>
                  {priorityOptions.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Start Date {required}</label>
                <DateInput className="form-control" value={modalTask.start_date} onChange={(e) => setModalTask((c) => ({ ...c, start_date: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Due Date {required}</label>
                <DateInput className="form-control" value={modalTask.due_date} onChange={(e) => setModalTask((c) => ({ ...c, due_date: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Status</label>
                <select className="form-select" value={modalTask.status} onChange={(e) => setModalTask((c) => ({ ...c, status: e.target.value }))}>
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Progress (%)</label>
                <input type="number" min="0" max="100" className="form-control" value={modalTask.progress} onChange={(e) => setModalTask((c) => ({ ...c, progress: Number(e.target.value) }))} />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <button type="button" className="btn btn-light" onClick={closeTaskModal}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={onTaskSave}>{modalMode === "edit" ? "Save Changes" : "Create Task"}</button>
        </Modal.Footer>
      </Modal>

      {/* ---- Create / Edit project modal ---- */}
      {/* ไม่ใส่ overflow-hidden ที่ modal-content เพราะจะตัดรายการ dropdown ของ Select ที่เปิดลงล่างจนมองไม่เห็น */}
      <Modal show={!!projectForm} onHide={closeProjectModal} centered size={IS_SUPER_USER ? "lg" : undefined} contentClassName="border-0 shadow-lg">
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, rgba(12, 118, 229, 0.20), rgba(47, 213, 228, 0.18))", borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
          <Modal.Title className="fw-bold">{projectForm?.editId ? "แก้ไขโปรเจกต์" : "สร้างโปรเจกต์ใหม่"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-4">
          {projectForm && (
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Project Key {required}</label>
                <input className="form-control text-uppercase" placeholder="เช่น xxx" value={projectForm.key} onChange={(e) => setProjectForm((c) => ({ ...c, key: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">ชื่อโปรเจกต์ {required}</label>
                <input className="form-control" placeholder="เช่น xxx" value={projectForm.name} onChange={(e) => setProjectForm((c) => ({ ...c, name: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Status</label>
                <Select
                  className="form-control"
                  value={projectForm.status}
                  options={projectStatusOptions}
                  placeholder="เลือกสถานะ"
                  default={false}
                  onChange={(value) => setProjectForm((c) => ({ ...c, status: value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">ประเภทงาน</label>
                <Select
                  className="form-control"
                  value={projectForm.platform}
                  options={platformOptions.map((o) => ({ value: o, label: o }))}
                  placeholder="ไม่ระบุ"
                  onChange={(value) => setProjectForm((c) => ({ ...c, platform: value }))}
                />
              </div>
              {/* ---- ผู้รับผิดชอบ: เลือกพนักงาน + บทบาท ได้หลายคน ---- */}
              <div className="col-12">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="form-label mb-0">ผู้รับผิดชอบ</label>
                  <button type="button" className="btn btn-sm btn-light fw-semibold" style={{ fontSize: 12 }} onClick={addMember}>
                    <i className="fa-solid fa-user-plus me-1" />เพิ่มคน
                  </button>
                </div>
                {projectForm.members.length === 0 && (
                  <div className="text-secondary rounded-3 p-2 text-center" style={{ fontSize: 12, border: "1px dashed #cbd5e1" }}>
                    ยังไม่ได้ระบุ — กด "เพิ่มคน" เพื่อเลือกพนักงานและบทบาท (ผู้รับผิดชอบ / PM / Dev / Tester)
                  </div>
                )}
                {projectForm.members.map((member, idx) => {
                  // คนที่ลาออกไปแล้วแต่ยังอยู่ในโปรเจกต์ ต้องยังเห็นชื่อในตัวเลือก
                  const options = memberOptions.map((e) => ({ value: e.uid, label: e.nickname ? `${e.nickname} (${e.name})` : e.name }));
                  if (member.employee_uid && !memberOptions.some((e) => e.uid === member.employee_uid)) {
                    options.push({ value: member.employee_uid, label: `${memberDisplayName(member)} (ไม่ได้ทำงานแล้ว)` });
                  }
                  return (
                    <div key={idx} className="d-flex align-items-center gap-2 mb-2">
                      <div style={{ flex: 2, minWidth: 0 }}>
                        <Select className="form-control" value={member.employee_uid} options={options} placeholder="เลือกพนักงาน" onChange={(value) => updateMember(idx, { employee_uid: value })} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Select className="form-control" value={member.role} options={memberRoleOptions} default={false} onChange={(value) => updateMember(idx, { role: value })} />
                      </div>
                      <button type="button" title="ลบคนนี้" className="btn btn-sm btn-light flex-shrink-0" onClick={() => removeMember(idx)}>
                        <i className="fa-solid fa-trash" style={{ color: "#dc2626" }} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* ---- ราคาเต็ม + งวดชำระเงิน (เฉพาะ super_user) ---- */}
              {IS_SUPER_USER && (() => {
                const installments = projectForm.installments || [];
                const pct = sumPercent(installments);
                const pctOk = installments.length === 0 || pct === 100;
                const pctColor = pct > 100 ? "#dc2626" : pctOk ? "#047857" : "#d97706";
                const paidAmount = installments.filter((i) => i.is_paid).reduce((sum, i) => sum + installmentAmount(projectForm.total_price, i.percent), 0);
                return (
                  <div className="col-12">
                    <div className="rounded-3 p-3" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="fa-solid fa-coins" style={{ color: "#b45309" }} />
                        <span className="fw-semibold" style={{ fontSize: 13, color: "#78350f" }}>ราคาและงวดชำระเงิน</span>
                        <span className="badge rounded-pill ms-1" style={{ background: "rgba(180,83,9,0.12)", color: "#b45309", fontSize: 10 }}>
                          <i className="fa-solid fa-lock me-1" />Super User เท่านั้น
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-5">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>ราคาเต็ม (บาท)</label>
                          <div className="input-group">
                            <input
                              className="form-control text-end"
                              inputMode="decimal"
                              placeholder="0.00"
                              value={projectForm.total_price ?? ""}
                              onChange={(e) => setProjectForm((c) => ({ ...c, total_price: cleanNumberInput(e.target.value) }))}
                            />
                          </div>
                          <div className="small text-secondary mt-1" style={{ fontSize: 11 }}>
                            {projectForm.total_price !== "" && projectForm.total_price !== null ? `= ${fmtMoney(projectForm.total_price)} บาท` : "ยังไม่ระบุราคา"}
                          </div>
                        </div>
                        <div className="col-md-7">
                          <label className="form-label mb-1" style={{ fontSize: 12 }}>รูปแบบงวดที่ใช้บ่อย</label>
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {installmentPresets.map((preset) => (
                              <button
                                key={preset.label}
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                style={{ fontSize: 11, borderRadius: 8 }}
                                onClick={() => applyInstallmentPreset(preset.parts)}
                              >
                                {preset.label}
                              </button>
                            ))}
                            <button type="button" className="btn btn-sm btn-primary" style={{ fontSize: 11, borderRadius: 8 }} onClick={addInstallment}>
                              <i className="fa-solid fa-plus me-1" />เพิ่มงวด
                            </button>
                          </div>
                          <div className="small text-secondary mt-1" style={{ fontSize: 11 }}>เลือกรูปแบบแล้วปรับ % แต่ละงวดได้ตามต้องการ</div>
                        </div>
                      </div>

                      {installments.length > 0 && (
                        <div className="table-responsive mt-3">
                          <table className="table table-sm align-middle mb-0" style={{ fontSize: 12 }}>
                            <thead>
                              <tr style={{ color: "#78350f" }}>
                                <th style={{ width: 36 }}>#</th>
                                <th>ชื่องวด</th>
                                <th style={{ width: 90 }} className="text-end">%</th>
                                <th style={{ width: 130 }} className="text-end">จำนวนเงิน</th>
                                <th style={{ width: 110 }} className="text-center">สถานะ</th>
                                <th style={{ width: 150 }}>วันที่ชำระ</th>
                                <th style={{ width: 36 }} />
                              </tr>
                            </thead>
                            <tbody>
                              {installments.map((inst, idx) => (
                                <React.Fragment key={idx}>
                                <tr style={{ background: inst.is_paid ? "rgba(16,185,129,0.08)" : "transparent" }}>
                                  <td className="border-bottom-0 text-secondary">{idx + 1}</td>
                                  <td className="border-bottom-0">
                                    <input className="form-control form-control-sm" style={{ fontSize: 12 }} placeholder={`งวดที่ ${idx + 1}`} value={inst.label} onChange={(e) => updateInstallment(idx, { label: e.target.value })} />
                                  </td>
                                  <td className="border-bottom-0">
                                    <input
                                      className="form-control form-control-sm text-end"
                                      style={{ fontSize: 12 }}
                                      inputMode="decimal"
                                      placeholder="0"
                                      value={inst.percent}
                                      onChange={(e) => updateInstallment(idx, { percent: cleanNumberInput(e.target.value) })}
                                    />
                                  </td>
                                  <td className="border-bottom-0 text-end fw-semibold">{fmtMoney(installmentAmount(projectForm.total_price, inst.percent))}</td>
                                  <td className="border-bottom-0 text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm rounded-pill fw-semibold"
                                      style={{
                                        fontSize: 11,
                                        padding: "2px 10px",
                                        background: inst.is_paid ? "rgba(16,185,129,0.16)" : "rgba(100,116,139,0.14)",
                                        color: inst.is_paid ? "#047857" : "#475569",
                                        border: "none",
                                      }}
                                      onClick={() => updateInstallment(idx, { is_paid: !inst.is_paid, paid_date: !inst.is_paid ? inst.paid_date || todayIso() : inst.paid_date })}
                                    >
                                      <i className={`me-1 ${inst.is_paid ? "fa-solid fa-circle-check" : "fa-regular fa-circle"}`} />
                                      {inst.is_paid ? "ชำระแล้ว" : "ยังไม่ชำระ"}
                                    </button>
                                  </td>
                                  <td className="border-bottom-0">
                                    <DateInput
                                      className="form-control form-control-sm"
                                      style={{ fontSize: 12 }}
                                      value={inst.paid_date || ""}
                                      disabled={!inst.is_paid}
                                      onChange={(e) => updateInstallment(idx, { paid_date: e.target.value })}
                                    />
                                  </td>
                                  <td className="border-bottom-0 text-end">
                                    <button type="button" className="btn btn-sm btn-link text-danger p-0" title="ลบงวดนี้" onClick={() => removeInstallment(idx)}>
                                      <i className="fa-solid fa-xmark" />
                                    </button>
                                  </td>
                                </tr>
                                <tr style={{ background: inst.is_paid ? "rgba(16,185,129,0.08)" : "transparent" }}>
                                  <td />
                                  <td colSpan={6} className="pt-0">
                                    <input
                                      className="form-control form-control-sm"
                                      style={{ fontSize: 12 }}
                                      maxLength={500}
                                      placeholder="หมายเหตุ (ถ้ามี)"
                                      value={inst.note || ""}
                                      onChange={(e) => updateInstallment(idx, { note: e.target.value })}
                                    />
                                  </td>
                                </tr>
                                </React.Fragment>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="fw-semibold" style={{ borderTop: "2px solid #fde68a" }}>
                                <td colSpan={2} className="text-secondary">รวม {installments.length} งวด</td>
                                <td className="text-end" style={{ color: pctColor }}>{pct}%</td>
                                <td className="text-end">{fmtMoney(installmentAmount(projectForm.total_price, pct))}</td>
                                <td colSpan={3} className="text-secondary" style={{ fontSize: 11 }}>
                                  ชำระแล้ว {fmtMoney(paidAmount)} · คงเหลือ {fmtMoney((Number(projectForm.total_price) || 0) - paidAmount)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                          {!pctOk && (
                            <div className="small mt-1" style={{ fontSize: 11, color: pctColor }}>
                              <i className="fa-solid fa-triangle-exclamation me-1" />
                              {pct > 100 ? "% รวมเกิน 100% กรุณาปรับให้ถูกต้อง" : `% รวมยังไม่ครบ 100% (ขาดอีก ${round2(100 - pct)}%)`}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {!projectForm?.editId && (
                <div className="col-12">
                  <p className="small text-secondary mb-0">สร้างแล้วจะเปิดโปรเจกต์ให้ทันที จากนั้นกด <strong>+ Task</strong> เพื่อเพิ่มงานในไทม์ไลน์</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          {projectForm?.editId && (
            <button type="button" className="btn btn-outline-danger me-auto" onClick={deleteProject}>
              <i className="fa-solid fa-trash me-1" />ลบโปรเจกต์
            </button>
          )}
          <button type="button" className="btn btn-light" onClick={closeProjectModal}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={saveProject}>{projectForm?.editId ? "บันทึก" : "สร้างโปรเจกต์"}</button>
        </Modal.Footer>
      </Modal>

      {/* ---- AI analysis modal ---- */}
      <Modal show={!!analysis} onHide={() => setAnalysis(null)} centered size="lg" contentClassName="border-0 shadow-lg overflow-hidden">
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #7b3fc0, #0f6bdc)" }}>
          <Modal.Title className="fw-bold text-white"><i className="fa-solid fa-wand-magic-sparkles me-2" />AI วิเคราะห์แผนงาน</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {analysis && (
            <>
              {/* โปรเจกต์ที่ควรโฟกัส */}
              {analysis.focus && (
                <div className="rounded-3 p-3 mb-3" style={{ background: "linear-gradient(135deg, rgba(123,63,192,0.10), rgba(15,107,220,0.10))", border: "1px solid #dbe4f0" }}>
                  <div className="small text-secondary mb-1">โปรเจกต์ที่ควรโฟกัสที่สุด</div>
                  <div className="fw-bold" style={{ fontSize: 16, color: "#0b4fa0" }}>
                    {analysis.focus.key} · {analysis.focus.name}
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <span className="badge rounded-pill" style={{ background: "rgba(15,107,220,0.12)", color: BRAND_BLUE }}>คืบหน้า {analysis.focus.progress}%</span>
                    {analysis.focus.overdue > 0 && <span className="badge rounded-pill bg-danger">เลยกำหนด {analysis.focus.overdue}</span>}
                    {analysis.focus.dueSoon > 0 && <span className="badge rounded-pill bg-warning text-dark">ใกล้ครบกำหนด {analysis.focus.dueSoon}</span>}
                    {analysis.focus.doing > 0 && <span className="badge rounded-pill" style={{ background: "rgba(15,107,220,0.12)", color: "#0b4fa0" }}>กำลังทำ {analysis.focus.doing}</span>}
                  </div>
                </div>
              )}

              {/* ลำดับงานที่ควรทำก่อน */}
              <div className="fw-semibold mb-2" style={{ color: "#475569" }}>ลำดับงานที่ควรทำก่อน (Top {analysis.ranked.length})</div>
              {analysis.ranked.length === 0 ? (
                <p className="text-success mb-0"><i className="fa-solid fa-circle-check me-1" /> ทุกงานเสร็จหมดแล้ว ไม่มีงานค้าง</p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {analysis.ranked.map((r, i) => {
                    const toneColor = r.tone === "overdue" ? "#dc2626" : r.tone === "soon" ? "#d97706" : r.tone === "doing" ? "#0b4fa0" : "#64748b";
                    const toneBg = r.tone === "overdue" ? "rgba(220,38,38,0.08)" : r.tone === "soon" ? "rgba(217,119,6,0.08)" : r.tone === "doing" ? "rgba(15,107,220,0.06)" : "#f8fafc";
                    return (
                      <div key={`${r.projectId}-${r.task.id}`} className="d-flex align-items-center gap-3 rounded-3 p-2 px-3" style={{ background: toneBg, border: "1px solid #eef2f7" }}>
                        <div className="fw-bold d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 26, height: 26, borderRadius: "50%", background: toneColor, color: "#fff", fontSize: 13 }}>{i + 1}</div>
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="fw-semibold text-truncate">
                            <span className="badge rounded-pill me-1" style={{ background: "rgba(15,107,220,0.12)", color: BRAND_BLUE, fontSize: 10 }}>{r.projectKey}</span>
                            {r.task.name}
                          </div>
                          <div className="small" style={{ color: toneColor }}>{r.reason}</div>
                        </div>
                        <div className="small text-secondary text-nowrap">
                          {format_date(r.task.start_date, "dd/mm/yyyy")} → {format_date(r.task.due_date, "dd/mm/yyyy")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* สรุปแต่ละโปรเจกต์ */}
              <div className="fw-semibold mt-3 mb-2" style={{ color: "#475569" }}>สรุปแต่ละโปรเจกต์</div>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr className="text-secondary">
                      <th>โปรเจกต์</th>
                      <th className="text-center">คืบหน้า</th>
                      <th className="text-center">เลยกำหนด</th>
                      <th className="text-center">ใกล้ครบ</th>
                      <th className="text-center">กำลังทำ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.projectSummaries.map((p) => (
                      <tr key={p.id}>
                        <td className="fw-semibold">{p.key} <span className="text-secondary fw-normal">· {p.name}</span></td>
                        <td className="text-center">{p.progress}%</td>
                        <td className="text-center" style={{ color: p.overdue ? "#dc2626" : undefined, fontWeight: p.overdue ? 700 : 400 }}>{p.overdue}</td>
                        <td className="text-center" style={{ color: p.dueSoon ? "#d97706" : undefined }}>{p.dueSoon}</td>
                        <td className="text-center">{p.doing}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="small text-secondary mt-3 mb-0">
                * วิเคราะห์จากสถานะ/กำหนดส่ง/ความสำคัญ ณ วันที่ {format_date(today, "dd/mm/yyyy")}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <button type="button" className="btn btn-light" onClick={() => setAnalysis(null)}>ปิด</button>
        </Modal.Footer>
      </Modal>

      {/* ---- Drag help overlay ---- */}
      {showDragHelp && (
        <>
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1050, backdropFilter: "blur(2px)" }}
            onClick={() => setShowDragHelp(false)}
          />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1051, background: "#fff", borderRadius: 16, padding: "24px 28px", width: "min(350px, calc(100vw - 32px))", maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(15,107,220,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-hand-pointer" style={{ color: BRAND_BLUE, fontSize: 16 }} />
              </div>
              <span className="fw-bold" style={{ fontSize: 15, color: "#1e293b" }}>วิธีจัดลำดับงาน</span>
              <button type="button" className="btn-close ms-auto" style={{ fontSize: 11 }} onClick={() => setShowDragHelp(false)} />
            </div>
            <ol className="ps-3 mb-3" style={{ fontSize: 13, color: "#475569", lineHeight: 2 }}>
              <li>วางเมาส์ที่ช่อง <strong style={{ color: "#1e293b" }}>No.</strong> ของแถวที่ต้องการย้าย</li>
              <li>กดค้างแล้ว <strong style={{ color: "#1e293b" }}>ลากขึ้น/ลง</strong> ไปยังตำแหน่งที่ต้องการ</li>
              <li>ปล่อยเมาส์เพื่อวาง — ระบบบันทึกลำดับใหม่อัตโนมัติ</li>
            </ol>
            <div className="rounded-3 p-2 px-3 mb-3 d-flex align-items-start gap-2" style={{ background: "rgba(15,107,220,0.07)", border: "1px solid rgba(15,107,220,0.18)" }}>
              <i className="fa-solid fa-lightbulb mt-1" style={{ color: "#f59e0b", fontSize: 13, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: "#475569" }}>เส้นสีน้ำเงินจะแสดงตำแหน่งที่จะวางงาน</span>
            </div>
            <button type="button" className="btn btn-primary w-100 fw-semibold" style={{ borderRadius: 10 }} onClick={() => setShowDragHelp(false)}>
              เข้าใจแล้ว
            </button>
          </div>
        </>
      )}

      {/* ---- Task edit help overlay ---- */}
      {showTaskHelp && (
        <>
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1050, backdropFilter: "blur(2px)" }}
            onClick={() => setShowTaskHelp(false)}
          />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1051, background: "#fff", borderRadius: 16, padding: "24px 28px", width: "min(350px, calc(100vw - 32px))", maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(15,107,220,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-pen-to-square" style={{ color: BRAND_BLUE, fontSize: 16 }} />
              </div>
              <span className="fw-bold" style={{ fontSize: 15, color: "#1e293b" }}>วิธีแก้ไข Task</span>
              <button type="button" className="btn-close ms-auto" style={{ fontSize: 11 }} onClick={() => setShowTaskHelp(false)} />
            </div>
            <div className="d-flex flex-column gap-2 mb-3">
              <div className="rounded-3 p-2 px-3 d-flex align-items-start gap-2" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <i className="fa-solid fa-i-cursor mt-1" style={{ color: BRAND_BLUE, fontSize: 13, flexShrink: 0 }} />
                <div>
                  <div className="fw-semibold" style={{ fontSize: 13, color: "#1e293b" }}>คลิกที่ชื่อ Task</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>แก้ไขชื่อได้ทันทีแบบ Inline — คลิกออกหรือกด Enter เพื่อบันทึก</div>
                </div>
              </div>
              <div className="rounded-3 p-2 px-3 d-flex align-items-start gap-2" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <i className="fa-solid fa-computer-mouse mt-1" style={{ color: "#7b3fc0", fontSize: 13, flexShrink: 0 }} />
                <div>
                  <div className="fw-semibold" style={{ fontSize: 13, color: "#1e293b" }}>ดับเบิลคลิกที่แถว Task</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>เปิด Modal แก้ไขรายละเอียดทั้งหมด เช่น วันที่ สถานะ Assignee และ Progress</div>
                </div>
              </div>
              <div className="rounded-3 p-2 px-3 d-flex align-items-start gap-2" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <i className="fa-solid fa-toggle-on mt-1" style={{ color: "#10b981", fontSize: 13, flexShrink: 0 }} />
                <div>
                  <div className="fw-semibold" style={{ fontSize: 13, color: "#1e293b" }}>Dropdown สถานะ</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>คลิก Dropdown ในคอลัมน์ Status เพื่อเปลี่ยนสถานะได้เลย</div>
                </div>
              </div>
            </div>
            <button type="button" className="btn btn-primary w-100 fw-semibold" style={{ borderRadius: 10 }} onClick={() => setShowTaskHelp(false)}>
              เข้าใจแล้ว
            </button>
          </div>
        </>
      )}

      <style>{`
        .gantt-row { transition: background 0.12s ease; }
        .gantt-row:hover td:not([style*="sticky"]) { background: #eef4ff; }
        .gantt-row:hover { background: #eef4ff; }
        .pf-row:hover > div { background: #f2f7ff !important; }
        .summary-card { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .summary-card:hover { transform: translateY(-3px); box-shadow: 0 22px 48px rgba(15, 23, 42, 0.12); }
      `}</style>
    </AppShell>
  );
}
